// NO @symbo-ls/workspace-shared read here — not even a deferred-call
// wrapper. This state module is INVOKED at module scope below (the
// newBooking seed calls todayISO()), and under the current parcel cycle it
// evaluates BEFORE workspace-shared's default export settles — the wrapper
// fired eagerly and `_wsShared.functions` threw at parcelRequire time,
// booting every dev.my page BLACK (2026-08-12). A state seed must stay
// init-order-independent, so the trivial local-date format is inlined
// (same output as shared dateUtils.todayISO).
const todayISO = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const _today = new Date()

export { DOC_FOLDERS, KB_CATEGORIES } from '@symbo-ls/workspace-documents/constants.js'

export default {
  isRootState: true,

  currentUserEmail: '',
  currentUserId: '',
  signedIn: false,
  companyName: 'Symbols',

  // Auth claims + org context — populated by subscribeAuthSession from the
  // governance JWT. Without these declared, smbls 3.14 silently drops the
  // updates and org switching / People filter / role gates all break.
  jwtClaims: null,
  // Live AI model diagnostics (shared/functions/aiProviderDiagnostics.js —
  // server-wide, not chat-scoped). Undeclared, the write was dropped
  // outright (fault 2 of the diagnostics ticket).
  aiProviderDiagnostics: null,
  orgs: [],
  workspaces: [],
  org: null,
  workspace: null,
  _wsSwitching: null,
  // Current workspace's load phases; pending/failed apps must never look like
  // an empty successful install list. Wiped with the workspace below.
  _workspaceActivation: null,
  loading: false,
  // Optimistic-pick override for AppWorkspaceSwitcher. When the user
  // clicks an org in the dropdown, this gets set to the picked org id
  // so `_activeOrg(root)` (and thus the Trigger label + checkmark)
  // reflects the choice this same tick instead of waiting for the
  // server's JWT refresh. Cleared once the workspace-change handler
  // fires (or on switch error).
  _pendingActiveOrgId: null,
  _wsRefreshing: false,
  _loginLoading: false,
  orgNotifCount: {},
  orgNotifSignals: {},
  selectedGroup: null,
  selectedRole: null,
  // ⚠️ DECLARED 2026-08-20 (WS-SWITCH-INCOMPLETE-1 step 6) BECAUSE NOTHING
  // COULD SEE IT. `selectedTeamCategory` is the third key PersonCard.js's
  // `_selField` helper can name, alongside the two above — and all three are
  // written as `s.root.update({ [_selField(s.kind)]: s.name })`, a COMPUTED
  // key. It had no default anywhere, so the closed-world partition never had
  // it in its universe; and its only write is computed, so the root-write
  // census could not see it either. It was created by first write and was
  // invisible to BOTH instruments at once. It does not leak — its value is the
  // binary member-status lens 'active' | 'inactive', a vocabulary the source
  // defines — but "does not leak" and "nothing is watching" are different
  // facts, and only the first one was ever true here.
  // ⇒ Declared here so the partition can see it, and filed in
  // WORKSPACE_UNSCOPED_KEYS (state.js) with that reason.
  selectedTeamCategory: null,
  peopleQuery: '',
  // A plain seed — the page's location is never read while this module
  // LOADS. A test or tool that imports it under a partial `window` (no
  // `location`) passed the old `typeof window` guard and threw on
  // `window.location.pathname` at import time (authLoginBounceBreaker /
  // foreignOrgUrl, 2026-09-02). The shell's state.js seeds '/' over this key
  // anyway, and the router writes the real path on every navigation.
  currentPath: '/',
  // SEARCH-1 (2026-08-14): bumped by shellApp's onRouteChanged (app.js) on
  // EVERY el.router() navigation — the one reactive "the URL may have
  // changed" signal single-shell-root-instance components (TicketsDetailModal
  // chief among them) can subscribe to, since they never get a fresh
  // onCreate from the router's own content-swap.
  _routeNudge: 0,
  // HeaderTabs re-derives this route on navigation. It may include tenant
  // record ids, so the old workspace's active marker must not survive a switch.
  _navActivePath: null,
  navSearchQuery: '',
  navSearchResults: [],
  _navSearchGrouped: [],
  _navSearchLoading: false,
  _navSearchOpen: false,
  // ⌘K FILTER CHIPS (CMDK-05): `[{ field, value, label, fieldLabel }]` — the
  // app-record values the palette is filtered by (functions/search/
  // globalSearch.js runSearchAction). Tenant values (a customer, a wallet
  // kind), so workspace-scoped below; a closed search drops them.
  _navSearchChips: [],
  // THE ONE SEARCH DROPDOWN (9A — DESIGN-WL-PAGESLOT-OPEN-SPLIT-SEARCH-1): in
  // this shell the launcher (components/SearchAppsMenu.js) hosts the typed
  // results itself, so the header search's own results panel (shared
  // HeaderSearch `Dropdown`) stays hidden here — one dropdown, never two, and
  // never a second panel flashing in when the launcher closes. A capability,
  // not a state: declared true, never written. A shell that does not declare
  // it (canvas standalone's MainNavbar) keeps the header's own panel.
  _searchResultsInLauncher: true,
  // A home widget's "+" form, hosted at the shell root (components/
  // HomeWidgetFormHost.js — pick 10 C: "a widget + opens only its form,
  // never the whole page"): { form, moduleId, widgetId, seq } while a
  // module form is mounted, null otherwise.
  _homeWidgetForm: null,
  // THIS SESSION's decision about the window's WIDTH (the PageSlot sheet's
  // resize handle — functions/slotSheetWidth.js setSlotSheetWidth): a percent
  // of the band once the person decides, null until then (the sheet then
  // reads the record, `userPrefs.slotSheetWidth`, then this device's copy).
  // A PERSON's choice, unscoped like `_homeLook`.
  _slotSheetWidth: null,
  // THIS SESSION's width of the /chat rail (chat functions/chatSidebarResize.js
  // — the handle on the rail's inner edge): its share of the page row in
  // percent, null until the person drags (the rail then shows its `G`). A
  // PERSON's layout choice, unscoped like `_slotSheetWidth`.
  _chatSidebarWidth: null,
  // The split view's divider (canvas/components/PageSlot.js
  // PageSlotSplitHandle — drag, arrow keys, double-click back to 50): the
  // first pane's percent of the band, 30–70. Split sizing belongs to the
  // frame, never to an app or a route: a PERSON's layout choice, unscoped
  // like `_slotSheetWidth` (state.js WORKSPACE_UNSCOPED_KEYS).
  _slotSplitRatio: 50,
  // The launcher's category filter (components/SearchAppsMenu.js
  // SearchAppCategory): a searchAppGroupsFor group key, 'all' by default. A
  // view filter with no workspace data in it, unscoped with the other UI
  // inputs (state.js); a group the next workspace lacks reads as 'all'.
  _launcherGroup: 'all',
  // The open launcher's search model (functions/searchApps.js, the note above
  // launcherTileGroups): 'trigger' when a navbar chevron or the home App strip
  // opened it, 'companion' when the focused header search did. Written on
  // every open; one launcher layer exists at a time.
  _launcherMode: 'companion',
  // The launcher's OWN filter text — the field a trigger-opened panel shows
  // (components/SearchAppsMenu.js SamFilterField). Never the header search's
  // `navSearchQuery`; a trigger open resets it (functions/searchApps.js
  // openSearchAppsDropdown), and the companion never reads it.
  _launcherFilter: '',
  // SEARCH-1: keyboard-selected row index in the grouped results dropdown
  // (flat index across every group's items, matching NavSearchResultGroup's
  // flatten order) — -1 means no row is keyboard-selected.
  _navSearchActiveIndex: -1,
  // /admin/teams invite feedback (GA-W3.2) — seeded so the status/link/copy
  // factories register their deps at first render (absent-key trap).
  _teamInviteEmail: '',
  _teamInviteStatus: null,
  _teamInviteLinkCopied: false,
  // Workspace-invite card (GA-W2.1 UI) — same seeding rule.
  _wsInviteEmail: '',
  _wsInviteRole: 'editor',
  _wsInviteTeams: {},
  _wsInviteStatus: null,
  _wsInviteLinkCopied: false,
  navUnreadCounts: {},
  contentSection: null,

  // Anchored mini-profile popover (bottom-left presence dock avatars, and any
  // other person-avatar trigger that reuses it) — el.call('openPersonPopover',
  // email, anchorEl) in functions/personPopover.js stashes the target email
  // here right before opening the @symbo.ls/dropdown layer; PersonPopoverCard
  // (components/PersonPopover.js) reads it back reactively. Same "pointer on
  // root, read live" contract as profileEmail / _personDetail / selectedTicket.
  _personPopoverEmail: null,
  // The display name the OPENER knows for that email (a mail sender header)
  // — beats the bare address when the popover mints a Party from it.
  _personPopoverName: null,
  // Party-by-email resolution cache (§3.7 Directory, functions/
  // partyLookup.js): absent = never asked, `false` = resolved with no
  // match (gates "Add to directory"), object = the party summary.
  _partyByEmail: {},

  people: [],
  // FALSE-ZERO-HOME-NON-AGENT-KEYS-1 — the roster's POSITIVE load marker and
  // its error key. `people` is seeded `[]` above, so `Array.isArray(people)`
  // is true from the first frame and can never mean "loaded": measured on the
  // real schema default, a loaded 2-agent roster against the seeded `[]`
  // rendered a confident "2" team headcount and a 100%-agents share bar.
  // Written by every people producer — the shell-mount prime peek (app.js),
  // the boot loader and the workspace-switch reload (loadPrivateData.js), and
  // Home's Refresh (pages/main.js). `peopleError` records the boot fetch
  // failure that was previously only console.warn'd, so a failed roster read
  // renders '!' instead of the same 0 an empty workspace shows.
  peopleLoaded: false,
  peopleError: null,
  peopleAll: [],
  teams: [],
  teamsByEmail: {},
  // See state.js — the userId-keyed team-membership index that resolves for
  // non-mgmt viewers whose roster email is redacted (fable.md P0 regression).
  teamsByUserId: {},
  orgMembersByOrgId: {},
  // Pending WorkspaceInvitation rows for the active workspace — see
  // functions/pendingInvitations.js. Admin/owner-gated; a member's
  // fetch is skipped client-side (server 403s it anyway).
  pendingInvitations: [],
  // See state.js — `loadTeamMembers`' idempotency guard key. Undeclared keys
  // are dropped by smbls 3.14 root.update(), which broke the guard and caused
  // a team-members request storm.
  teamMembersLoaded: {},
  userDocs: [],
  userPrefs: null,
  resourceLinks: [],
  // (Removed 2026-06-03: _wsMenuOpen — AppOrgSwitcher migrated to
  // @symbo.ls/dropdown; the plugin owns visibility state internally.)
  announcements: [],
  selectedAnnouncement: null,
  // Broadcast newsroom UI. The records remain in `announcements`; these are
  // local view/editor controls so a workspace switch never carries content.
  _broadcastScope: 'internal',
  _broadcastTag: 'all',
  _broadcastQuery: '',
  _broadcastVisibleCount: 50,
  _broadcastDraft: null,
  _broadcastSaving: false,
  _broadcastSaveError: null,
  birthdays: [],
  companyInfo: [],
  roles: { available: [], byUser: {}, version: 0 },

  // ── Calendar ──
  viewMode: 'week',
  currentYear: _today.getFullYear(),
  currentMonth: _today.getMonth(),
  currentDay: _today.getDate(),
  currentMinutes: _today.getHours() * 60 + _today.getMinutes(),
  selectedEvent: null,
  showNewBooking: false,
  showEventDetail: false,
  // (Removed 2026-06-01: `confirmAction` migrated to @symbo.ls/windows
  // `confirmDialog`. Callers now `el.call('confirmDialog', {title, message,
  // confirmText, confirmTheme, icon, onConfirm, onCancel})` instead of
  // writing a state field. No state declaration needed.)
  selectedPerson: null,
  profileEmail: null,
  profileModalTab: 'profile', // 'profile' | 'availability' | 'settings' — used by the unified ProfileModal
  dragPreview: null,
  quickCreate: null,
  hoverTime: null,
  teamMembers: [],
  // AddTeammatesModal (calendar Team view) — `teamMembers`' roster-editing
  // satellites. Declared here, not only in the calendar eager slice, so the
  // workspace-switch wipe can read their reset values from this file's
  // defaults; the slice seeds the same values at boot.
  showAddTeammates: false,
  addTeammateQuery: '',
  addTeammateFeedback: '',
  onlineUsers: [],
  newBooking: {
    title: '',
    date: todayISO(),
    startTime: '09:00',
    endTime: '10:00',
    invitees: '',
    inviteeQuery: '',
    description: '',
    type: 'meeting',
    meetingMode: 'symbols-meet',
    recurring: 'none',
    recurringDays: [],
    editingEventId: null
  },
  events: [],

  // ── Reminders ──
  // Personal and workspace-shared reminders (records plane, collection:
  // 'reminders') — see functions/reminders.js. `remindersScope` selects the
  // visible plane (Private is the safe default); filter/draft keys stay UI-only
  // and intentionally remain outside WORKSPACE_SCOPED_KEYS below.
  reminders: [],
  // FALSE-ZERO-HOME-NON-AGENT-KEYS-1 — same `[]`-seed problem as `people`:
  // the Home Reminders widget rendered "0 due today" before its own fetch had
  // ever resolved. Written by both producers — Home's inline records-plane
  // fetch (pages/main.js) and the /reminders page fetch (pages/reminders.js).
  // Distinct from `_remindersLoadError` below, which is the records-plane
  // CAPABILITY gate (403) and carries its own "not enabled" copy.
  remindersLoaded: false,
  remindersError: null,
  remindersScope: 'private',
  _remindersFilter: 'all',
  _reminderDraft: '',
  _reminderDraftDue: null,
  _reminderDraftShared: false,
  // Set by the /reminders fetch's onFetchError when the records-plane
  // capability gate 403s (forbidden_collection_not_granted) — drives the
  // "Reminders isn't enabled for this workspace" empty-state copy instead
  // of a broken page (reminderEmptyText, functions/reminders.js).
  _remindersLoadError: false,

  // ── History ──
  // Built-in History app (/history): the workspace activity log as a
  // timeline. The rows are the audit log of ONE workspace — tenant data, in
  // the workspace scope cascade below with their load marker, their error
  // key, the person filter (an email off this workspace's roster) and the
  // open-card map (keyed by day + email). The type filter is a vocabulary
  // the source defines, byte-identical in every workspace, and stays
  // unscoped (state.js) like `remindersScope`.
  historyEvents: [],
  historyLoaded: false,
  historyLoadError: false,
  historyPerson: null,
  historyType: null,
  _historyExpanded: {},

  // ── Getting started → Team ──
  // POB S9 (/getting-started/team, functions/gettingStartedTeam.js): the
  // people the onboarding research read off the company's own team page,
  // the owner's typed invites to them and the company-domain join switch.
  // All of it is ONE workspace's data, in the workspace scope cascade below;
  // the switch tail re-reads it while the page is on screen.
  // The read: idle | loading | ready | soon (no server yet) | denied | error.
  gettingStartedTeamStatus: 'idle',
  gettingStartedPeople: [],
  gettingStartedDomain: '',
  // { [personId]: { email, on, status, error } } — what the OWNER typed;
  // never prefilled. Every write carries all four fields (a write REPLACES
  // the object a key holds — smbls 3.14.870 store — so a left-out field
  // would be lost).
  gettingStartedInvites: {},
  // "Invite someone else by e-mail" — a person the research did not find.
  gettingStartedOther: { email: '', status: '', error: '' },
  gettingStartedSending: false,
  gettingStartedSendResult: null,
  // { domain, enabled, eligible } from the server; null = no switch.
  gettingStartedDomainJoin: null,
  gettingStartedDomainJoinStatus: '',
  _gettingStartedWhyOpen: false,

  // ── Getting started → Imports ──
  // POB S10 (/getting-started/imports, functions/gettingStartedImports.js):
  // the files and pages the onboarding research found on the company's own
  // site, the owner's own upload, the open preview and its column → field
  // mapping, the run. ONE workspace's data, in the workspace scope cascade
  // below; the switch tail re-reads it while the page is on screen.
  // The read: idle | loading | ready | soon (no server yet) | denied | error.
  gettingStartedImportStatus: 'idle',
  gettingStartedSources: [],
  // The installed apps' collections an import may fill (the server's list).
  gettingStartedTargets: [],
  // The open review — { previewId, name, total, columns }, what the preview
  // holds — or null on the list.
  gettingStartedReview: null,
  // The owner's choice for it — { targetKey, mapping } — its own key, so a
  // pick never replaces the review the column list renders from. `mapping`
  // holds EVERY column ('' = skip), so a write never leaves a stale column
  // behind.
  gettingStartedMapping: null,
  // The last refusal: unsupported_file | too_large | no_table_found |
  // unavailable | failed, or ''.
  gettingStartedImportError: '',
  gettingStartedUploading: false,
  // { importId, status: starting|running|done|failed|pending, created,
  // skipped, failed, undo: ''|undoing|done|failed } or null.
  gettingStartedRun: null,

  // ── Tickets ──
  columns: [],
  tickets: [],
  loadedByColumn: {},
  totalByColumn: {},
  // The /tickets sidebar TYPE counts (PR #283) — written beside totalByColumn
  // by tickets/pages/board.js; see fetchTypeCounts (tickets ticketLoaders.js).
  totalByType: {},
  // PR #287's raw per-type x per-column counts behind totalByType
  // (fetchTypeCounts writes them; the Task Settings modal and the kanban
  // re-sum them without a refetch when a column is hidden or excluded).
  // null = not loaded yet: every reader guards `if (raw)`.
  _rawTypeCounts: null,
  // The board's status beside its counts: true while the /tickets board page
  // is mounted (tickets/pages/board.js onCreate / onRemove). The registered
  // counts refresh (el.call('refreshTicketsCounts')) reads nothing while it is
  // false — PR #287 kept a FUNCTION on root for this instead.
  _ticketsBoardLive: false,
  loadingColumn: {},
  myTickets: [],
  // ── Home dashboard load state (FALSE-ZERO-HOME-NON-AGENT-KEYS-1) ──
  // All five were created by FIRST WRITE and lived in no state tier, so the
  // partition could not see them. Declared here in the same commit that
  // drains them from __tests__/fixtures/rootWriteUndeclared.js.
  //
  // `_userTickets` is Home's OWN ticket cache, written by _ensureHomeTickets
  // (pages/main.js). It exists because `tickets` above is the /tickets BOARD
  // cache and has no Home producer at all: the agent status caption read
  // `Array.isArray(root.tickets) ? length : 0`, a permanent 0 on every cold
  // Home load, which rendered an agent as confidently idle while the user had
  // open work. `_userTickets` is seeded `null` ("not read yet", like
  // `_agents`) and becomes an array — possibly `[]` — only when a fetch
  // resolves; `_homeTicketsLoaded` is the producer's one-shot REQUEST gate
  // (set before the fetch, so it can never be the load marker);
  // `_homeTicketsError` records the failure the loader previously only
  // console.warn'd.
  _userTickets: null,
  _homeTicketsLoaded: false,
  _homeTicketsError: null,
  // Workspace presence for the Home analytics widget + the Watcher agent
  // caption — a DEDICATED key, never the Projects-scoped `_analytics`
  // (PRESENCE-ZERO-1). `null` is "not read yet"; a loaded payload's own
  // metrics may each be `null` when the server omitted the field, which is
  // what stops an ABSENT field from becoming a well-formed 0 at the data
  // layer the way `Number(res.usersNow) || 0` did.
  _homeWorkspaceActivity: null,
  _homeWorkspaceActivityError: null,
  recentTickets: [],
  // /tickets/feed's activity cache (tickets/pages/feed.js, FeedMain.js).
  // null (not []) is the page's own "not fetched this session yet" sentinel
  // — FeedMain reads `s.root.feedItems == null` to show the loading state
  // instead of an empty-state flash. Previously undeclared (relied on smbls
  // 3.14's create-signal-on-first-write); declared here so it has a real
  // schema-empty default and WORKSPACE_SCOPED_KEYS below can single-source
  // its workspace-switch reset value instead of hand-typing it separately.
  feedItems: null,
  myViewQueueOpen: false,
  _activityData: {},
  // WS-SWITCH-INCOMPLETE-1 (2026-08-20): the heatmap's DRILL-DOWN pair, declared
  // here for the first time. Both were created by FIRST WRITE
  // (tickets/components/ActivityHeatmap.js) and lived in NO defaults tier, so
  // they sat in the root-write census baseline instead of the partition's
  // universe — the closed-world check could not fail on either. Declaring them
  // is what lets them be scoped at all.
  // ⛔ BOTH DEFAULT TO null, NOT {} / []. `_activitySelectedDate` is what the
  // details panel's `hide:` reads, so a truthy empty would reopen an empty
  // panel over the new tenant; `_activityDetails` distinguishes "still
  // loading" (null) from "loaded, no events" ([]) in the very next `hide:`
  // line, and a `[]` default would make the skeleton never show.
  _activitySelectedDate: null,
  _activityDetails: null,
  _dailyDraft: '',
  _dailyUpdates: [],
  // WS-SWITCH-INCOMPLETE-1 step 3 (standup domain) — which history cards are
  // expanded, as ONE map keyed by row id. This used to be an UNBOUNDED family
  // of root keys (`_standupOpen_<created_at|date>`, written by
  // `root.update({ [key]: !root[key] })` in StandupsMain.js). A key whose NAME
  // is computed at runtime can never appear in a static registry, so neither
  // the wipe nor the root-write census could ever see it — it was residue that
  // survived every switch by construction. One declared map fixes both: the
  // wipe reaches it and the census counts it. Same move as `_ticketComments`'
  // slot tag in step 4 — a fact about ROOT state has to live on root.
  _standupOpenIds: {},
  _standupUserName: '',
  _standupPhase: 'idle',
  _standupSummary: '',
  _standupActions: [],
  _standupFollowUps: [],
  // Local date the standup gate was last evaluated against — lets the
  // rollover interval in TicketsDailyUpdate.onCreate detect day-changes
  // on tabs left open overnight without firing a network call every tick.
  //
  // ⭐ WS-SWITCH-INCOMPLETE-1 — SCOPING THIS KEY CHANGED ITS GATE, IN THE SAME
  // DIFF. The stamp used to mean "a day was checked"; the interval fired on
  // `stamp && stamp !== today`, so an EMPTY stamp meant "never poll". A wipe
  // sets it empty — scoping it alone would have disarmed the only poller the
  // standup plane has, permanently, for the rest of the session. The gate is
  // now `stamp !== today`, and the stamp means "this plane was derived for the
  // ACTIVE workspace, today". So the wipe un-stamps it and the SAME interval
  // re-derives the plane for the new workspace on its next tick — the switch's
  // PHASE-2 refetch for this domain, with no second mechanism. This is the
  // mirror of the tenant-guard rule found in the approval domain: a wipe can
  // orphan the handler that a key's own correctness depended on, so the wipe
  // and the handler must ship together.
  _standupCheckedDate: '',
  _ticketComments: [],
  // The open comment editor is part of that ticket thread, including its
  // unsaved content. Clear both halves when the workspace changes.
  _editingCommentId: null,
  _commentEditDraft: '',
  // TicketDetailModal's mail reply belongs to the current ticket/workspace.
  _mailReplySending: false,
  // WS-SWITCH-INCOMPLETE-1 step 4 — which ticket's comments occupy the SINGLE
  // shared `_ticketComments` slot. This used to be `el._lastCommentsTicketId`,
  // an ELEMENT-LOCAL memo of a ROOT-LEVEL fact, and that mismatch is what made
  // `_ticketComments` unsafe to wipe: a workspace switch empties the slot but
  // cannot reach an element property, so the memo kept naming the OLD tenant's
  // ticket and the guard skipped the refetch that would have refilled it.
  // Living here, it is wiped by the same registry entry as the slot it
  // describes — the memo is tenant-correct by construction, not by a teardown
  // call somebody has to remember to add.
  _ticketCommentsFor: null,
  // Comment paging for that slot: server rows fetched so far (the next older
  // page's offset), whether the server has older rows, and the in-flight flag
  // for "Load more comments". They describe the slot, so they wipe with it.
  _ticketCommentsSkip: 0,
  _ticketCommentsHasMore: false,
  _ticketCommentsLoadingOlder: false,
  // The first page of the slot is in flight — the thread shows a comment-shaped
  // skeleton instead of an empty list until it lands.
  _ticketCommentsLoading: false,
  _mentionModal: { open: false },
  // TicketDetailModal transient UI state — declared so initial reads
  // (before any write) get a defined value instead of `undefined`,
  // which simplifies the binding factories that compare against
  // primitives (`s.root._approvalPending ? 'not-allowed' : 'pointer'`).
  // smbls 3.14 creates signals for undeclared keys on first write, so
  // this isn't a typed-bag whitelist — just sensible defaults.
  _aiTicketLoading: false,
  // Renders TicketDetailModal as a dedicated full-bleed page instead of an
  // overlay when true. Toggled by the /ticket/:id page on mount; the
  // existing modal styles react to flip backdrop + Window sizing.
  _ticketFullPage: false,
  // WS-SHELL-MOUNT-TREE-BUILD-2700MS-1 — the ModalRoot `if:` latch. False at
  // boot so the 246-element modal host is NOT built inside the boot task;
  // bootShell flips it once, on its bounded post-mount `yieldToPaint`.
  // One-way and app-lifetime: deliberately NOT in any workspace-scoped wipe
  // tier — re-arming it would destroy the live dialog host on a tenant switch.
  _modalHostReady: false,
  // WS-SHELL-BOOT-TASK-LOAF-ACCEPTANCE-1 — the staged-mount ladder counter
  // (functions/shellStages.js). 0 at boot so every off-screen top-level
  // subtree evaluates its `if:` false and is NOT built inside the mount task;
  // the shell root's onCreate then walks it 1 → 2 → 3, one macrotask apart,
  // so the shell's one long build becomes several bounded ones. Monotonic and
  // one-way, same tier as `_modalHostReady` above: re-arming it on a tenant
  // switch would evaluate a built subtree's latch false and destroy it.
  _shellStage: 0,
  // The ticket detail modal's module has been loaded and folded into the
  // registry (functions/ticketDetailModalLoad.js). Gates the TicketsDetailModal
  // `if:` latch with the need; one-way and app-lifetime like the two above -
  // the folded registry survives tenant switches, and a wipe would only make a
  // built modal's latch read false.
  _ticketModalReady: false,
  _approvalPending: false,
  _approvalError: null,
  _approvalNote: '',
  // WS-SWITCH-INCOMPLETE-1 (2026-08-20): the post-decision pill's pair. Both
  // were UNDECLARED — written at root by the approve/reject success paths and
  // by nothing else, so smbls 3.14 minted their signals on first write and no
  // tier ever named them. They have no reset of any kind, which is why a
  // decision made once suppressed the Approve button everywhere afterwards.
  // ⛔ `null`, never `''`: ApprovedPill's own gate is `_approvalDoneAt &&
  // !_isEditing`, and `new Date('')` is Invalid Date — an '' would still be
  // falsy at the gate but would print `APPROVED · Invalid Date` the moment any
  // future read stops gating on presence first.
  _approvalDoneAt: null,
  _approvalKind: null,
  _assigneeQuery: '',
  // The assignee field has focus: opens its suggestion list before the first
  // keystroke (tickets/components/TicketDetailModal.js). Declared, because a
  // root key created by its first write is not reactive for an effect that read
  // it before the write, and the root-write census refuses it.
  _assigneeFocused: false,
  // The option the arrow keys stand on in a ticket people list:
  // `{ kind: 'reporter' | 'assignee', index }`, or null for none. One key for
  // both fields, tagged with its field, so the two lists never light each
  // other's rows. Declared for the same reason as the flag above.
  _peopleFieldActive: null,
  // null = idle (show the current reporter's name); a string = the user is
  // actively typing a query. The reporter value factory (`if (q != null)`) and
  // the edit-sync guard (`_reporterQuery == null`) both treat null as idle, so
  // this MUST default to null, not '' — an '' default counts as "typing" and
  // left the reporter input blank on first edit of a freshly opened ticket (#111).
  _reporterQuery: null,
  _commentTab: 'comment',
  _ticketDeps: {},
  // WS-SWITCH-INCOMPLETE-1 step 5 — the "Add dependency" picker's candidate
  // pool and its two in-flight flags. All three were created by FIRST WRITE
  // (`root.update({ _depPool: docs })` in TicketDetailModal.js's _loadDepPool)
  // and declared in NO state-defaults tier, so they were outside
  // scopes.partition.test.js's UNIVERSE and the closed-world check could not
  // see them at all. Declaring them is what makes them classifiable.
  //
  // ⛔ `_depPool` DEFAULTS TO null, NOT []. `_freshEmpty` (scopes.js) restores
  // the DECLARED default, and the picker refills only when
  // `!s.root._depPool && !s.root._depPoolError` — a truthiness test. A `[]`
  // default would wipe to `[]`, which is TRUTHY, so the guard would never fire
  // again and the picker would show zero matches for the rest of the session.
  // The empty of a value a GUARD consumes must be falsy; step 3's
  // `_wsInviteRole` is the same lesson read the other way round.
  _depPool: null,
  _depPoolLoading: false,
  _depPoolError: null,
  filterAssignee: null,
  // The /tasks board's ?project=<id> scope (tickets/pages/board.js
  // boardUrlScope). Workspace-scoped — see WORKSPACE_SCOPED_KEYS below.
  filterProjectId: null,
  filterLabel: null,
  searchQuery: '',

  // ── Requests ──
  // Empty so empty-state UX + SDK fetch wiring own what users see;
  // any fixture seed would ship to every signed-in user.
  customerRequests: [],
  inboundRequests: [],
  outboundRequests: [],
  auditReports: [],

  // ── Resolutions ──
  // Same reasoning as the request arrays above.
  improvementRequests: [],
  editRequests: [],
  selectedTicket: null,
  ticketDraft: null,
  ticketEditMode: false,
  activeDrag: null,
  dropPreview: null,
  syncing: false,

  // ── Chat ──
  // One-shot latch: flips true when ensureChatContext folds the full chat
  // context (components + functions) into the live registry. Gates the
  // MessengerOverlay's `if:` mount (app.js) so the ~215KB chat UI tree is
  // neither bundled eagerly nor built pre-fold. Deliberately NOT in the
  // workspace-scoped wipe registry — the folded registry survives tenant
  // switches, so the latch must too.
  _chatCtxFolded: false,
  // Latched once the documents context folds (the /files note editor folds it
  // outside any /documents route). Deliberately NOT workspace-scoped: the
  // component registry keeps folded definitions across tenant switches.
  _documentsCtxFolded: false,
  // Meet-side twin of the latch above: flips true when ensureMeetContext
  // (functions/meetFold.js) folds the deferred meet context in for a surface
  // that needs a meet component key OFF a meet route — today chat's
  // channel-header group call, whose modal body extends MeetVideoRoom. Same
  // survives-tenant-switch reasoning, so it is likewise NOT wipe-registered.
  _meetCtxFolded: false,
  // Mail-side twin of the chat latch: flips true when ensureMailContext
  // (functions/mailFold.js) folds the deferred mail context in and fills the
  // MailOverlaySlot — the header envelope opens the docked MailOverlay from
  // ANY route, long before /mail ever mounts. Same survives-tenant-switch
  // reasoning, so it is likewise NOT wipe-registered.
  _mailCtxFolded: false,
  // Set before navigating to /chat to auto-open a DM with the given email.
  // Consumed and cleared by chat's onCreate once channels are loaded.
  _pendingDmEmail: null,
  channels: [],
  channelMembers: [],
  activeChannelId: null,
  showNewChannel: false,
  newChannelDraft: { name: '', topic: '', kind: 'channel', is_private: false, members: [] },
  messagesByChannel: {},
  activeThreadRootId: null,
  presence: {},
  typingByChannel: {},
  myMentions: [],
  collapsedGroups: {},
  searchOpen: false,
  searchResults: [],
  mentionPicker: null,
  sidebarDropdown: { open: false },
  contextDropdown: null,
  _hiddenChannels: [],
  _pinnedChannels: [],
  renameModal: { open: false },
  pendingAttachments: {},
  composerPreview: false,
  composerReplyTo: {},
  // Edit-in-place state for chat messages. When set, the Composer switches
  // to edit mode, pre-fills with _editingMsgBody, and submits via
  // saveEditMessage (functions/chat/messageActions.js).
  _editingMsgId: null,
  _editingMsgBody: '',
  // The message list's "New messages" / "Jump to latest" pill
  // (chat/components/MessageList.js JumpLatest): when the reader left the
  // newest row, the channel they left it in, and each channel's unread span
  // captured on open (functions/markRead.js — a map keyed by channel id).
  _chatAwaySince: null,
  _chatAwayCid: null,
  _chatNewFrom: {},
  // The composer whose emoji picker is open (its __composerId,
  // chat/components/Composer.js), and whether the thread reply field holds
  // text (chat/components/ThreadPanel.js — its send button).
  _composerEmoji: null,
  _threadHasText: false,
  // Who is typing where (CHAT-TYPING-1, chat/functions/chatTyping.js):
  // { [channelId]: { [email]: { email, userId, thread, until } } }, filled
  // by the chat stream's `chat.typing` frames. NOT `chatTyping` — that key is
  // the AI assistant's "working" flag (its composer spinner reads it).
  _chatTypists: {},
  chatVisibleCounts: null,
  activeTab: 'conversation',
  showFilterBar: false,
  filters: {
    authorEmail: null,
    hasAttachments: false,
    hasMentions: false,
    query: ''
  },
  // Messenger drawer legacy slice (shared/components/Messenger.js) — a
  // separate mini chat surface from the /chat page above, same tenant
  // boundary. Previously undeclared (relied on smbls 3.14's create-signal-
  // on-first-write, which meant Messenger's `s.root.update({...})` writes
  // to these were at risk of being silently dropped by the typed-bag guard
  // until first touched); declared here for a real schema-empty default +
  // single-sourced workspace-switch reset value (WORKSPACE_SCOPED_KEYS below).
  _chatDrawerChannels: null,
  _chatDrawerChannelId: null,
  _chatDrawerMessages: null,

  // ── Documents ──
  activeSection: 'notes',
  selectedFolder: 'all',
  selectedDocument: null,
  docsQuery: '',
  documents: [],
  // First-load gate for the documents Count UI — see DocsMain.Count
  // factory. Avoids the "0 documents" flash before the fetch resolves
  // (WORKSPACE-DOCS-COUNT-RESET).
  _documentsLoaded: false,
  selectedNoteId: null,
  notesFilter: 'all',
  notes: [],
  editorDoc: null,
  editorMode: null,
  _docSaving: false,
  // The reader's own reading settings for a note — { size, leading, measure }
  // or null when never chosen (documents functions/notesReader.js). Hydrated
  // per person from user_preferences, this device's copy painting first.
  notesReader: null,
  // A note card drag in flight (DocsCard) and the sidebar row it hovers: a
  // folder row takes the note into that folder, Shared / Private set its
  // privacy (documents components/DocsSidebarExport.js), a chat row
  // (`chat:<id>`) shares the note there (chat functions/chatNoteDrop.js).
  _notesDragId: null,
  _notesDropKey: null,
  // The share-to-chat picker (documents functions/noteShare.js): the note it
  // shares ({ id, kind, title, privacy } or null when closed), its search,
  // and the conversation a send is in flight to (null when none).
  _noteShareTarget: null,
  _noteShareQuery: '',
  _noteShareBusy: null,
  // The note a reference opened beside the open one in the reading view —
  // { kind, id, from } or null when that split is closed (documents
  // functions/notesRefs.js). Session only.
  _notesRef: null,
  kbQuery: '',
  selectedCategory: 'all',
  kbArticles: [],

  // ── Files ──
  // The @symbo-ls/workspace-files context is deferred out of the eager
  // sharedLibraries merge (its ~105K of components + functions load only on
  // /files via lazySharedLibPage), so its state.js no longer seeds on every
  // route. pageContext.js reads root.fileNodes on BOTH /files AND /infra, so the
  // desktop's persisted state stays eager here — mirroring how documents' state
  // lives in this file. The lazySharedLibPage merge re-seeds files/state.js on
  // /files with add-missing semantics, so these declarations win (no double-seed).
  fileNodes: [],
  fileDocs: [],
  filesOpenFolder: null,
  _fileNodePos: {},
  _fileNodesLoaded: false,
  // The failed fileCanvas fetch's message (files/functions/fetchConfigs.js,
  // or the 'files.empty.error' key), null otherwise: FilesMain's empty state
  // reads it. Workspace-scoped (WORKSPACE_SCOPED_KEYS below).
  _fileNodesError: null,
  _filesUploading: [],

  // ── Meet ──
  rooms: [],
  activeRoomId: null,
  meetView: 'list',
  liveParticipants: {},
  waitingRoom: {},
  livekitParticipants: [],
  livekitSpeakers: {},
  livekitConnected: false,
  meetCallInFlight: false,
  meetCallError: null,
  // WS-SWITCH-INCOMPLETE-1 step 3, bucket A2 (2026-08-21): the error's two
  // satellites. Both were UNDECLARED — connectCall writes `err.name` /
  // `err.code` beside `err.message` (MEET-ERROR-COPY-LIES-1) and nothing
  // else names them, so smbls minted their signals on first write and the
  // partition one tier up could not see them. Declared `null`, like the
  // message they travel with; registered scoped beside it below.
  meetCallErrorName: null,
  meetCallErrorCode: null,
  // WS-SWITCH-INCOMPLETE-1 leg 14 (2026-08-21): the error record GREW from a
  // triple to a QUINTUPLE after step 3 shipped. `livekitErrorFields` now also
  // carries `reasonName` and the HTTP `status` (the 429 quota rejection
  // arrives as `err.context.status`), connectCall co-writes both in its
  // pre-flight reset AND its catch, and VideoRoom reads them straight into
  // the overlay's copy. Both were UNDECLARED — the census named them by
  // hand. Declared `null`, like the three they travel with; registered
  // scoped beside them below so the LOCKSTEP is whole again.
  meetCallErrorReason: null,
  meetCallErrorStatus: null,
  // (Removed 2026-06-01: `showNewRoom` migrated to @symbo.ls/windows
  // openNewRoomModal layer. `newRoomDraft` below retained as the data
  // carrier the body component still reads via _draft(s).)
  showRoomSettings: false,
  showLiveCaptions: true,
  shareLinkCopied: false,
  newRoomDraft: { name: '', kind: 'room', transcribeEnabled: true, isPrivate: false },
  utterances: [],
  analysis: null,
  // The transcript view's "its rows answered" flag (meet TranscriptPage's
  // fetch transform sets it; a room switch clears it — meet pages/index.js
  // _planHashReseed, meet functions/meetAnswer.js meetAnswerOpen).
  transcriptLoaded: false,
  analyzing: false,
  analyzeError: null,
  transcriptPending: false,
  _meetAnalyzeInFlight: false,
  meetChatMessages: [],
  meetChatUnread: 0,
  showMeetChat: false,
  // FLY IN A CALL (meet functions/meetFly.js, the reads in meetFlyState.js):
  // the call's Fly state — { v: 2, roomId, mode (the room's switch: null or
  // { on, by, at }), open (this person's theater over the call), focus,
  // hidden, pages: [{ key, roomId, agent, by, mine, prompt, at, page }] };
  // `_meetMention` the `@…` being typed in the call chat (the agent
  // suggestions). Both this call's — workspace-scoped below.
  meetFly: null,
  _meetMention: null,
  // THE PERSON CALLED RINGS (functions/incomingCall.js): { roomId, callerId,
  // at } while a fresh private room this person is a MEMBER of rings, else
  // null — read by components/IncomingCallBanner.js. Workspace-scoped below.
  _callRing: null,
  // WHAT CHANGED DURING THE CALL (meet functions/meetCallChanges.js): { roomId,
  // since, at, groups: [{ key, label, appKey, route, added, updated }] } — the
  // workspace's timeline since the call began, by app; read by the call's live
  // summary and its end screen. Workspace-scoped below.
  _callChanges: null,
  // Toggles MeetTranscriptPanel — the in-call full-transcript side
  // drawer. Distinct from `showLiveCaptions` which controls the small
  // 3-line caption OVERLAY at the bottom of VideoRoom.
  showMeetTranscript: false,
  // WS-ROOTWRITE-CENSUS-CHAT-ROOM-KEYS-UNDECLARED-1 (2026-10-18): the rooms
  // sidebar's ARCHIVE FAMILY. All four were UNDECLARED — the sidebar's Edit
  // and Archived controls (meet components/Sidebar.js, RoomList.js), the
  // archive stub (meet functions/archiveRooms.js) and the /meet page seed
  // (meet pages/index.js) write them at root, so smbls minted their signals on
  // first write; the root-write census named them once 11a38e3a3 rewrote the
  // sidebar. Declared with the values every reader already falls back to
  // (`|| []`, falsy). `isEditingRooms` / `selectedRooms` / `viewingArchived`
  // are registered scoped below; `archivedRoomsIds` unscoped, in state.js.
  archivedRoomsIds: [],
  isEditingRooms: false,
  selectedRooms: [],
  viewingArchived: false,

  // ── Lightbox ──
  lightbox: null,

  // ── AI Assistant ──
  assistantOpen: false,
  assistantMessages: [],
  assistantTyping: false,
  // aiChat thread picker (AppAssistantThreadPicker.js + functions/
  // aiAssistantThreads.js). Threads are WORKSPACE-scoped (2026-07-14 per
  // Nika) — wiped + refetched on workspace switch in loadPrivateData's
  // onWorkspaceChange, same convention as the chat isolation switch.
  _aiThreads: [],
  _aiThreadId: null,
  _aiThreadsLoading: false,
  _aiThreadsLoadAttempted: false,
  _aiThreadMenuOpen: false,
  // ASK-USER decision cards — ticket rows requiring Nika's approval/rejection/clarification.
  // Populated by fetchAskUserDecisions on AppAssistant init + kept live via
  // subscribeAskUserDecisions realtime. Cleared per-ticket when the user responds.
  askUserDecisions: [],
  // Set of ticket ids already dismissed this session so resolved cards don't reappear.
  _dismissedDecisionIds: [],
  // Clarify/reject inline form state.
  _decisionClarifyId: null,
  _decisionClarifyText: '',
  _decisionClarifyIntent: null,

  // ── Shell PageSlot (unified 2026-07-10) ──
  // `_canvasSlot` is the single route the shell PageSlot opens IN-PLACE — set
  // from any surface (home widgets, /studio marketplace, /infra, the /tickets
  // rail, global search) to mount that route's page via `el.set(pageDef)`. On
  // home the URL stays '/' with a ?page=<route> restore param. Full route path
  // (e.g. '/chat', '/tickets-page'); resolution lives in
  // canvas/components/PageSlot.js (_defFor → root._slotResolve → context.pages).
  // Cleared on close. Replaced the bespoke home AppWindow's `selectedPage` — the
  // two slot systems were folded into this one.
  _canvasSlot: null,
  // Cross-page hand-off for the Infra canvas — `{ domainFlow }` parked by
  // shared/functions/openProjectDomainFlow.js (Dropdown.Envs' "Custom domain"
  // row), lifted into root.infra.domainFlow and cleared by infraInit on the
  // next /infra mount. Declared so the partition (scopes.partition.test.js)
  // knows the key; classified UNSCOPED with the other transient overlay flags.
  _infraIntent: null,
  // Second PageSlot pane (side-by-side split view — cmd/ctrl+click opens here).
  _canvasSlotSecond: null,
  // The opt-in pane pin — '_canvasSlot' | '_canvasSlotSecond' | null (Nika
  // 2026-10-10: "Yes, opt-in pin"). The pane a user ("Keep this pane open" in
  // the pane header) or a module (openAppPage(route, { pin: true })) keeps: a
  // navbar/launcher open lands BESIDE it (functions/searchApps.js). Session
  // only — never persisted, never in the URL; in WORKSPACE_SCOPED_KEYS below.
  _slotPinned: null,
  // Minimize-to-dock (2026-08-06 per Nika "we should support minimize feature
  // for apps, to continue exactly where it left off"): ordered (oldest →
  // newest) parked-app entries { href, title, icon, wsId, minimizedAt }, cap
  // 5. The PARKED SURFACES themselves are keep-alive host elements inside the
  // PageSlot (canvas/components/PageSlot.js `SlotKeep_*`) — display-gated,
  // never unmounted while listed here, so restoring reveals the exact same
  // element. This list is what the MinimizedDock chips render, persisted
  // per-workspace to sessionStorage (shared/functions/minimizedSlots.js) so a
  // reload offers the same apps as COLD restores. In WORKSPACE_SCOPED_KEYS
  // below — a tenant boundary wipes the list, and the PageSlot host sweep
  // (wsId-stamped) unmounts the kept-alive DOM with it.
  _minimizedSlots: [],
  // PageSlot close-finish re-render nudge (see canvas/components/PageSlot.js +
  // the el.update({}) ticket in tickets/smbls.md).
  _slotNudge: 0,
  // Edge-to-edge toggle for the shell PageSlot (the Header's FullscreenBtn).
  _slotFullscreen: null,
  // Per-pane PageSlot back/forward history — { stack: [href, …], index }
  // (2026-08-06 per Nika "when we navigate accross pageslots - we shuld also
  // support back and forward navigations in pageslots"). The pure model lives
  // in canvas/functions/slotHistory.js; the pane Slot's lifecycle records
  // every slot path change (canvas/components/PageSlot.js _recordSlotHist),
  // and the pane header's arrows consume. Pane-scoped by design: the primary
  // (`_canvasSlot`) and the split second (`_canvasSlotSecond`) never share a
  // stack, so back/forward in one pane cannot move the other. In
  // WORKSPACE_SCOPED_KEYS below — a history names tenant routes.
  _slotHistPrimary: null,
  _slotHistSecond: null,
  // Per-pane PageSlot apps rail (the header's sidebar toggle — in-sheet quick
  // access to every slot-hostable app, fed by the same searchAppGroupsFor
  // source the launcher uses). Reset on pane close by PageSlot's close paths.
  _slotRailPrimary: false,
  _slotRailSecond: false,
  // Transient AI answer views (render_answer_ui): '/answer/<slug>-<seq>' →
  // page def, written by canvas/functions/answerUi.js and resolved as the
  // FIRST PageSlot tier. Session-only — never persisted, never in the router.
  _aiAnswerPages: null,
  // ── The assistant's generated board (DESIGN-WL-ASSISTANT-MAIN-VIEW-1,
  // functions/assistantBoard.js). `_aiBoard` is the board on screen in Page
  // mode — a view model: status, the validated spec and its cards, the pins,
  // the previous board kept under a failure. `_aiBoardHistory` the recent
  // boards behind the thread chips (max 5); `_aiBoards` the saved boards
  // (`ai.boards.list`) the boards row shows. `_aiBoardPreview` the UI mode
  // that shows the view without the Page-mode/home gate (the fixture board
  // until `ai.boards.generate` exists). All four in WORKSPACE_SCOPED_KEYS
  // below.
  _aiBoard: null,
  _aiBoardHistory: [],
  _aiBoards: [],
  _aiBoardPreview: false,
  // The board opened EXPLICITLY — by a `?board=<id>` link or a board tab
  // (assistantBoardSelect): it shows on the home whatever the placement and
  // the mode, until Esc / Back to home / Dismiss (assistantBoardDismiss).
  // Session-only; workspace-scoped (it names workspace A's board).
  _aiBoardExplicit: null,
  // ── The assistant's LIVE DECK (Presentation mode, AI_ASSISTANT_SPEC §12.1;
  // functions/assistantDeck.js). `_aiDeck` is the deck on screen — a view model
  // like `_aiBoard`: status, the DeckSpec as it streams, its slides (each
  // content slide's items derived by the board's own validation), the slide on
  // the stage (`index`), the save state, the route it was made on.
  // `_aiDeckPreview` the UI mode that shows the contract's fixture deck (before
  // the server's deck path answers); `_aiDeckPreviewIndex` that preview's slide.
  // All three in WORKSPACE_SCOPED_KEYS below.
  _aiDeck: null,
  _aiDeckPreview: false,
  _aiDeckPreviewIndex: 0,
  // ── FLY MODE (docs/FLY_MODE.md; functions/assistantFly.js). `_aiFly` is the
  // session of composed pages: { v, workspaceId, threadId, open, path, focus,
  // pages }. Workspace-scoped (its pages are built from A's apps and read A's
  // rows): in WORKSPACE_SCOPED_KEYS below.
  _aiFly: null,
  // Local Fly navigation and picker state belong to the active workspace.
  _aiFlyWorkspace: null,
  // The Fly rail put away (true) or shown (false); null = this device's last
  // choice (functions/assistantFly.js assistantFlyRailHidden). A person's UI
  // choice, the same in every workspace: in WORKSPACE_UNSCOPED_KEYS (state.js).
  _aiFlyRailHidden: null,
  // Phone navigation is local to the active workspace. The viewport flag is
  // device state; page/menu/composer state must never cross workspaces.
  _isPhoneViewport: null,
  _phoneNavPanel: null,
  _phoneNavError: null,
  _phoneNavOperation: null,
  _phoneComposerOpen: false,
  // The shell's own route history for the phone Back on a full-page app route
  // (functions/phoneNavigation.js phoneNavigationBack): the panes' shape,
  // { stack, index } (canvas/functions/slotHistory.js). A workspace's own
  // trail: a switch wipes it.
  _phoneNavHist: null,
  _filesRailOpen: false,
  _canvasRailOpen: false,
  // The module rail as a phone drawer (app.js ModuleRailHost): opened from
  // the dock's Page button on module routes without a rail of their own.
  _moduleRailOpen: false,
  // What THIS shell renders, as its first read found it
  // (functions/assistantRecords.js assistantClientCapabilities):
  // `{ records: 1 | 0 }` — the records renderer AND the SDK's
  // `ai.boards.records`. The board request and every chat turn declare it to
  // the server's capability gate. null until that read answers. A fact about
  // this page's SDK, not about a workspace.
  _aiClientCaps: null,
  // The assistant's RECORDS BLOCK answers (AI_ASSISTANT_SPEC v1 §2–§3,
  // functions/assistantRecords.js): `ai.boards.records` rows per
  // workspace + block ({ status, answer }), read once and shared by the
  // board cards and the thread's inline blocks — the 40 newest. `_aiBlockSaves`
  // the Save-as-board state of an inline block (saving | saved | failed).
  // Both hold workspace data: WORKSPACE_SCOPED_KEYS below.
  _aiRecords: {},
  _aiBlockSaves: {},
  // The AI answers' FORM requests (functions/assistantAppForms.js): per
  // request id { status: 'open' | 'saved', saved? } — the app's own form was
  // opened from a proposal, and the app reported the row it saved. Workspace
  // A's records: WORKSPACE_SCOPED_KEYS below.
  _aiForms: {},
  // The app record rows that failed to mount this session, keyed
  // `<moduleId>/<row component>` (functions/assistantRecords.js
  // assistantRecordsRowMount): a marked row falls back to the generic list
  // row. Names workspace A's installed apps: WORKSPACE_SCOPED_KEYS below.
  _aiRecordRowsBroken: {},
  // The minimized island's "Answer ready" marker — the id of the answer the
  // person dismissed (functions/assistantAnswerPopover.js; the island's head,
  // shared _simone/ReasoningPill.js).
  // Workspace-scoped with the thread it names (WORKSPACE_SCOPED_KEYS below).
  _aiAnswerPopoverDismissed: null,
  // The main thread's URL, `?ai=<conversationId>` (functions/
  // assistantThreadUrl.js). `_aiThreadUrlId` is the conversation the address
  // bar and the thread last AGREED on; `_aiThreadLink` marks a link while it
  // loads ({ id, status: 'loading', ws }) and when this person cannot read
  // it ({ id, status: 'unavailable', ws }) — `ws` is the workspace the read
  // ran in. Both name a conversation of ONE workspace: WORKSPACE_SCOPED_KEYS
  // below.
  _aiThreadUrlId: null,
  _aiThreadLink: null,
  // ── The home top (DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1) ──
  // `_homeSignals` — HOME_SIGNALS_CONTRACT v1's in-memory cache and pass
  // state for the ACTIVE workspace (functions/homeSignals.js): each provider's
  // last answer, the calls in flight, the providers expected, the first pass
  // window. Workspace-scoped (WORKSPACE_SCOPED_KEYS below). `_homeLook` — this
  // session's own decisions about the look of the home top (App strip style,
  // names, summary style, sky; functions/homeLook.js): a PERSON's choice,
  // unscoped like `themeMode` (state.js WORKSPACE_UNSCOPED_KEYS).
  _homeSignals: null,
  _homeLook: null,
  // The home's context-menu tip is dismissed (its ×, or seen once for a few
  // seconds — functions/homeCtxTip.js, persona audit WH-9). A PERSON's
  // device choice, mirrored from the `smbls_pref__homeCtxTipDismissed` cookie
  // at boot (app.js): unscoped like `_homeLook` (state.js
  // WORKSPACE_UNSCOPED_KEYS).
  _homeCtxTipDismissed: false,
  // The AI source of the Brief (functions/homeSummaryAi.js): the one ask for
  // the settled signals — { key, ws, status, text, chips, generatedAt }.
  // Workspace data (the summary names workspace A's counts):
  // WORKSPACE_SCOPED_KEYS below.
  _homeSummaryAi: null,
  // The home's last Ask and Create mode, for ONE workspace
  // (functions/homeView.js homeViewGroupSelect): { workspaceId, ask, create }.
  // Modes only, no content. WORKSPACE_SCOPED_KEYS below.
  _homeViewRecent: null,
  // The mode a home TAB set, for ONE workspace (functions/homeView.js, BLD-13
  // "Change only the home view"): { workspaceId, mode, barMode }. Never
  // persisted; an explicit pick clears it (setAssistantMode), and off the home
  // homeModeSync gives the person's pick back. WORKSPACE_UNSCOPED_KEYS
  // (state.js): it must outlive a switch to undo itself there.
  _homeMode: null,
  // A module page's "hide the shell composer" latch — { moduleId, pane, route }
  // (functions/moduleShellComposer.js, el.call('setShellComposer', false)).
  // Holds only while that page is live. Names workspace A's module:
  // WORKSPACE_SCOPED_KEYS below.
  _moduleComposerHidden: null,
  // The Fly start screen's "Get started" (functions/assistantFlyFirstRun.js):
  // the server's activation answer for the ACTIVE workspace — { ws, status,
  // at, data, code, wasOpen }. Workspace data: WORKSPACE_SCOPED_KEYS below.
  _flyFirstRun: null,
  // The cost before a build (functions/addAppEstimate.js; server CORE-EST):
  // the estimate of the builder's next turn for ONE place — { key ('ws|thread'),
  // at, full, min, waived, allocationExceeded, canStart, enough }. Numbers,
  // flags and ids only, never the draft. Workspace data (a balance verdict and
  // a waiver of workspace A): WORKSPACE_SCOPED_KEYS below.
  _addAppEstimate: null,
  // The workspace builds itself (functions/workspaceSetup.js, lane WS1): the
  // setup job of the ACTIVE workspace as the builder's first thread shows it —
  // { ws, status, at, job, code, arrived, back, opened, opening, retrying,
  // policy, me, site, build, apps }. Workspace data: WORKSPACE_SCOPED_KEYS below.
  _workspaceSetup: null,
  // A personalized sign-up's build board (functions/onboardingBuild.js, POB
  // S6): the server's board of the ACTIVE workspace — { ws, status, at, board,
  // code, since, timedOut, retrying, note, canRetry }. Workspace data:
  // WORKSPACE_SCOPED_KEYS below.
  _pobBuild: null,

  // ── AI parameters picker ──
  // Open/close gate for the CanvasPromptTextarea ParamsBtn dropdown.
  // `_aiModePickerTick` is bumped on every mode/authMode selection so
  // the dropdown's `children:` factories (which read sdk.ai.getMode /
  // getAuthMode) re-evaluate and the active checkmark moves immediately.
  _aiModePickerOpen: false,
  _aiModePickerTick: 0,
  // Viewport-space anchor for the position:fixed picker. Captured by
  // ParamsBtn.onClick from the button's getBoundingClientRect so the
  // picker can render outside CanvasPromptTextarea's `overflow: hidden`
  // and still align under (above) the button. Shape: { right, bottom }
  // (both in px). Null until the first open.
  _aiModePickerAnchor: null,
  // Becomes true once the shared/functions/aiService singleton has
  // resolved at shell boot (see app.js shellApp.onCreate). Reactive
  // factories that depend on the AI service gate on this so they
  // re-fire when it lands. The service ITSELF lives in the singleton
  // (not state) because smbls' state clone strips the class prototype.
  _aiSvcReady: false,
  // Proposals from sdk.ai.dispatch when intent === 'action' and the
  // user is in authMode='ask'. AiActionConfirmCard reads this; Confirm
  // fires sdk.ai.executeActions(proposals) and writes the results to
  // _aiActionResults, then clears the proposals.
  _aiActionProposals: null,
  _aiActionRunning: false,
  _aiActionError: null,
  _aiActionResults: null,
  // Drawer width (px) driven by the ResizeHandle drag. null falls back
  // to the AssistantDrawer default (640px). Persisted across re-renders
  // within a session; not currently stored in user prefs (the previous
  // legacy AppAssistant didn't either — survives only as long as the
  // tab is open).
  assistantWidth: null,
  assistantResizing: false,
  // WHERE the assistant's answers show (AI_ASSISTANT_SPEC v1 §1 — the
  // PLACEMENT) — one of 'main' | 'right' | 'left' | 'minimized'. 'main' is
  // Page: the main thread over the home's grid area, the DEFAULT (Nika's pick
  // 6 A — "main thread = default"); 'right'/'left' dock the full drawer to
  // that edge and SHRINK the main content (never overlay); 'minimized'
  // collapses the drawer into the SimoneReasoningPill small view above the
  // composer. An explicit choice always wins over this default: hydrated on
  // boot from localStorage('symbols_assistant_dock'), then the user record
  // (functions/assistantDock.js); the default itself is never written up.
  // The shell mirrors it onto <html data-assistant-dock> so the injected
  // shell CSS can drive the layout (reactive layout props freeze at mount in
  // v3.14 — see app.js).
  assistantDock: 'main',
  // Session-only: the dock was PROMOTED out of Page mode ('main') by an opener
  // (the header star, an "Ask AI" link) that asked for the transcript — the
  // close edge returns to Page (functions/assistantDock.js). null when the
  // dock is the user's own choice. Never persisted.
  _assistantDockPromotedFrom: null,
  // WHAT the assistant produces (AI_ASSISTANT_SPEC v1 §1) — 'fly' (every ask
  // composes a page; THE DEFAULT, Nika 2026-10-22: "ok make fly default
  // view" — the home opens on Fly) | 'assistant' (a conversation with
  // embedded live blocks) | 'dashboard' (a generated board) | 'tools' |
  // 'apps' | 'presentation'. The placement (`assistantDock` above) is WHERE
  // it shows. Written only by setAssistantMode (functions/assistantMode.js);
  // persisted per person (localStorage + user_preferences.assistantMode, the
  // record wins), so it is unscoped like `assistantDock` (state.js
  // WORKSPACE_UNSCOPED_KEYS). A person's own choice always wins over this.
  assistantMode: 'fly',
  // AGENT MODE (WS-AGENT-MODE-1, functions/agentMode.js): the workspace TUI
  // as the whole shell. `agentMode` is the person's choice (localStorage +
  // user_preferences.agentMode, the record wins; written only by
  // setAgentMode); `_agentModeLink` is a `?mode=agent` visit (no write).
  // `_agentModeRev` counts the shared runtime's renders: every frame factory
  // reads it, so one render re-runs exactly them (the runtime itself is the
  // plain `root.__agentModeRuntime`, outside the store). `_agentModeError`
  // holds why the model could not start. All four are unscoped (state.js):
  // the runtime re-creates itself on a workspace switch.
  agentMode: false,
  _agentModeLink: false,
  _agentModeRev: 0,
  _agentModeError: null,
  // The generated-board service answers for THIS workspace (set by the view
  // lane's vocabulary fetch, DESIGN-WL-ASSISTANT-MAIN-VIEW-1). Only the
  // DASHBOARD mode needs it (a send builds a board); Page — the placement —
  // needs nothing. Declared so every reader subscribes from first render.
  _aiBoardsAvailable: false,
  // The ai.boards VOCABULARY of the active workspace (installed apps ∩
  // grants) — the Page view's empty-workspace starter chips read it
  // (functions/assistantPageStarters.js); fetched lazily, once per
  // workspace. Declared so every reader subscribes from first render.
  _aiBoardVocab: null,
  // Session-only: the SimoneReasoningPill is acting as the assistant's
  // PERSISTENT handle even though the drawer is closed. Set by homeAiSend when
  // it deliberately suppresses the drawer (a page sheet is open in the PageSlot
  // and would be covered by it), cleared when the pill is expanded. Without it
  // the pill — which otherwise only shows while a turn is LIVE — vanished the
  // instant the turn finished, taking the user's message and the answer with it
  // (natali QA 2026-07-27: "sending a message while a generated view is open is
  // silently dropped"). Never persisted; a reload has no turn to surface.
  _assistantPillHandle: false,

  // ── simone chat surface — state keys consumed by extensions/packages/
  // simone components when imported into the workspace shell. Workspace
  // declares them here with safe defaults so DOMQL signal subscriptions
  // resolve (smbls 3.14 drops writes to undeclared root keys).
  //
  // Wire is sdk.ai (see functions/simoneAiTransport.js) — bridgeStatus
  // is forced to 'open' once the SDK service resolves so simone's
  // composer enables the Send button. bridgeUrl + target chrome show
  // the sdk.ai name (the picker dropdown is the real mode selector).
  bridgeStatus: 'connecting',
  bridgeUrl: 'sdk.ai',
  agent: 'sdk-ai',
  chatInput: '',
  chatMessages: [],
  chatTyping: false,
  pickedElements: [],
  // STRUCTURED AI ATTACHMENTS (VaultOS v2 design §4.1) — what the user HANDED
  // the assistant, as opposed to what it can infer. `{ refs: EntityRef[],
  // canvas: <spec §3.8 payload>|null }`; written only through
  // functions/aiAttached.js (attachAiRef / detachAiRef / attachAiCanvas /
  // clearAiAttached) and read by buildAiAwareness (the ATTACHED tier) and
  // simoneAiTransport (`context.attached` on the wire). Declared here because
  // v3.14 drops an undeclared root key on update() — an attach would have
  // rendered nothing and reported success.
  aiAttached: { refs: [], canvas: null },
  // CONFIDENCE BREAKDOWN DISCLOSURE (spec §4.3) — `{ [messageId]: true }`,
  // written by ChatMessage's confidence pill. Exactly the shape and purpose
  // `_aiToolOpen` carries for tool rows, declared rather than created by first
  // write so the root-write census (__tests__/rootWriteCensus.test.js) sees it
  // classified instead of adding it to the undeclared baseline.
  _aiConfidenceOpen: {},
  // A FAILED UNDO on a tool row (ChatToolRow UndoCreated / UndoFailure) —
  // `{ [createdId]: { cause } }`, the `_aiUndone` latch's twin: the cause
  // code (aiUndoCreated: gone · denied · network · unavailable · failed, the
  // row says it in the active language) and a "Try again" stay on the row
  // until a retry works.
  _aiUndoError: {},
  // ADVICE LEVEL DISCLOSURE (vault spec 06 §7) — `{ [messageId]: true }`,
  // written by ChatMessage's level pill. The confidence pill's twin, declared
  // for the same reason: v3.14 drops an undeclared root key on update().
  _aiLevelOpen: {},
  // A QUESTION CARD'S FACE (ChatCard PvCardChoice, Nika 2026-10-28) —
  // `{ [cardId]: { field: 'own' | 'edit' | null, change: boolean } }`: the
  // field open under its answers (Write my own / Edit) and an answered card
  // reopened by Change. UI state only, written with preventFetch.
  _aiAskCompose: {},
  picking: false,
  showSettings: false,
  currentAssistantId: null,

  // ── voice input (shared VoiceOverlay + functions/voiceSession.js —
  // architecture/VOICE_COMMANDS.md). Same undeclared-root-key rule as the
  // simone block above.
  voiceOpen: false,
  voiceMinimized: false,
  voiceMode: 'talk',
  // TRUE while the global composer's textarea holds focus (blur clears it on
  // a short delay so suggestion-chip clicks land first). Drives the
  // SimoneSuggestions focus-gated fade-in (2026-08-06 per Nika "show AI
  // recommendation buttons on focus instead of overlaying on pageslots").
  _promptFocused: false,
  // TRUE while the composer's "+" menu is open (its local `attachOpen`, mirrored by CanvasPromptTextarea): the AI
  // focus panel yields to it, one panel at a time (shared SimoneSuggestions, design-d2 8673ce440).
  _promptMenuOpen: false,
  // The OPEN dropdown layer's id (or true) — the shared openDropdown's root mirror, set by the layer's onOpen and
  // cleared by its onClose (design-d2, 2026-10-01). A UI transient: null on every boot, never persisted; the AI
  // focus panel reads it to yield under a menu (_simone/Suggestions.js).
  _dropdownLayer: null,
  // THE ALTERABLE AI REFERENCE (2026-08-26): holds the awareness refKey
  // ('record:<type>:<id>') of an open record the user EXCLUDED from the
  // prompt via its context chip. Keyed to one record identity, so opening a
  // different record self-invalidates it — never wiped, never persisted.
  _aiRefMutedFor: null,
  // THE COMPOSER'S AI CONTEXT (DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-INSPECT-1,
  // functions/aiScope.js): the member's explicit choice `{ key, at }` — a
  // context key ('page' | 'app' | 'workspace' | 'record' | 'app:<id>') and the
  // surface it was made on; null = the default (the most specific context).
  // Names this workspace's apps: wiped on a switch (state.js).
  _aiAskContext: null,
  // The focus panel's own word over its painting (shared functions/
  // aiPanelFocus.js): null (the resting rule) | 'open' (asked for) |
  // 'dismissed' (put away until the field is focused again). A UI transient.
  _aiPanel: null,
  // INSPECT & ASK (functions/aiInspect.js): the mode is on, and the outline's
  // measured box `{ x, y, w, h, label }` (null while nothing is under the
  // pointer). Off and empty after a switch.
  _aiInspect: false,
  _aiInspectBox: null,
  // INVITE FLOW (INVITE-ACCEPT-ACTIVATES-WORKSPACE-LIVE-1 /
  // INVITE-ONBOARDING-USERNAME-NOT-ORG-1): `_inviteFlowActive` marks the
  // accept page's beat so NoOrgOverlay never pushes org creation at an
  // invitee; `_usernamePromptPending` queues the one-time username nudge
  // (consumed by UsernamePromptGate AFTER the joined-workspace switch —
  // wiping it on that very switch would eat the nudge, hence unscoped).
  _inviteFlowActive: false,
  _usernamePromptPending: false,
  // Username-nudge modal draft carriers (functions/usernamePrompt.js +
  // AppUsernamePrompt) — same root-carrier pattern as _createOrg*.
  _unamePromptDraft: '',
  _unamePromptBusy: false,
  _unamePromptError: null,
  _unamePromptStatus: 'idle',
  // The server's explanation for a refusal (reserved word / format error) —
  // null when it sent none, which is the plain "already taken" case.
  _unamePromptReason: null,
  voiceChatKey: '',
  voiceLang: '',
  voiceStatus: 'idle',
  voiceInterim: '',
  voiceFinal: '',
  voiceResult: '',
  voiceError: '',
  voiceHostReady: false,
  // Which composer hosts the in-place voice strip: 'canvas' | 'chat' | null
  // (contract in shared/components/VoiceStrip.js — written by the composer
  // VoiceBtn before voicePttPress, AND-ed with voiceOpen by every reader).
  voiceHost: null,
  // Full text of the agent's last spoken turn (conversational engine).
  voiceReply: '',
  // Input-axis mic mute — voiceMicToggle flips it; every session opens unmuted.
  voiceMicMuted: false,
  // The call clock's zero (ms since epoch) — voiceStart sets it, every close
  // returns it to 0 (shared/components/VoiceControls.js VoiceCallClock).
  voiceStartedAt: 0,
  // THE canonical voice transcript, written by BOTH engines so no surface ever
  // learns which one is live: `{ id, role: 'user' | 'agent', text, streaming }`.
  // The one-shot pipeline mirrors the assistant thread into it (streaming
  // preserved); the conversational engine appends its own turns. Capped — see
  // appendVoiceTurn in shared/functions/voiceSession.js.
  voiceTurns: [],
  // Which engine owns the live session: null | 'convai' | 'recorder'. Mirrors
  // the scope-held handle into reactive state so the UI can say so honestly —
  // a failed conversational start must never look identical to a working one.
  voiceEngine: null,
  // WHY the conversational engine did not take this session:
  // null | 'command-mode' | 'no-agent' | 'unsupported-lang' | 'offline' |
  // 'start-failed'. Set by _startEngine — the source for the visible
  // talk-unavailable notice and the engine tag tooltip.
  voiceEngineReason: null,
  // Live active processes for the current turn — delegated/chained sub-agents
  // (agent.started/completed events via the SDK onProcessActive). The Chat
  // process panel renders this; reset per turn by the transport.
  activeProcesses: [],
  // Retained finished sub-agent runs (the multi-agent view). The transport
  // moves each turn's completed activeProcesses batch here (capped at 30)
  // instead of dropping it, so past parallel/chained runs stay reviewable.
  runHistory: [],
  // @-agent mention: the Simone composer's @-autocomplete picker
  // ({ query, start, end, options, activeIndex } | null). _targetAgentId /
  // _targetAgentName (the thread's directed agent) are created on write by the
  // existing YourAgentCard/ChatCard/team-and-agents set-sites; the picker key is
  // declared here so root.update never drops it.
  _simoneAgentPicker: null,
  perTabSessions: false,
  sessions: {},
  activeSessionId: null,
  showSessionMenu: false,

  // /add-app project-scoped chat context — one extension (= Project) = one
  // thread. Namespaced (_ax_*) so it never collides with the general chat above
  // (the drawer can be open on /add-app). _addAppProjectId = the active extension.
  // _addAppScope mirrors that extension's own scope ('private' | 'shared') so
  // the sidebar's Visibility tab + filtered Apps list stay in sync with
  // whichever thread is active (extensionSaveShare.js _persist and
  // selectAddAppExtension both write it). Was read/written in several places
  // (pages/add-app.js ScopeSwitcher, extensionSaveShare.js) without a declared
  // default — undeclared root keys risk a silently-dropped write, so it's
  // declared here rather than left to smbls 3.14's create-signal-on-first-write.
  _addAppProjectId: null,
  _addAppScope: 'private',
  // /add-app on a phone shows ONE pane: 'chat' | 'preview' | 'apps'
  // (functions/addAppPhonePane.js — persona round 5, marcus at 390 px).
  _addAppPhonePane: 'chat',
  // The way to open the app the builder just saved, shared or added
  // (functions/addAppSaved.js, BLD-28) — { notice, href, label }: the link of
  // the builder's line `_addAppSaveNotice` (it shows only beside ITS sentence,
  // `notice`). Names workspace A's app: WORKSPACE_SCOPED_KEYS below.
  _addAppSaveLink: null,
  // THE APP SPLIT (docs/APP_SPLIT.md): the two lists the project LIST
  // endpoint cannot tell yet — `{ wsId, extensions: [projectId], modules:
  // ['owner/key'], at }`, loaded by shared loadProjectKindSignals and read by
  // shared projectKindContext. One workspace's AI-app ids and catalog ids;
  // wiped on a switch (WORKSPACE_SCOPED_KEYS below).
  _projectKindSignals: null,
  // Draft carried by the composer '+' menu's "Build app" row — auto-sent as
  // the first build-chat message once /add-app's bridge opens (consumed by
  // pages/add-app.js BuildCanvas; written by functions/forceBuildApp.js).
  _addAppAutoSend: null,
  // Build MODE (2026-09-02 per Nika "should be toggle which runs this
  // functionality and opens that generated page after output"): while
  // armed, a send from either AI surface routes into the addapp build
  // pipeline (canvas composer branches in _sendPrompt; the sidebar rides
  // the scope flip in functions/_simone/sendPrompt.js) and
  // watchAiBuildOutput opens the build surface in the PageSlot when the
  // output lands.
  _aiBuildMode: false,
  _ax_chatMessages: [],
  _ax_chatTyping: false,
  _ax_currentAssistantId: null,
  _ax_activeProcesses: [],
  _ax_runHistory: [],
  _ax_sessions: {},
  _ax_activeSessionId: null,
  _ax_showSessionMenu: false,

  // Dashboard inline-thread chat context — prompting from the global
  // CanvasPromptTextarea on / answers ON the dashboard (a thread that replaces
  // the widget grid) instead of opening the side drawer. Namespaced (_db_*) so it
  // is its OWN thread, separate from the general drawer (like _ax_ for /add-app).
  // _homeThreadOpen toggles the inline thread vs the widget grid on /.
  // Since 2026-09-28 it is the MAIN THREAD's flag (AI_ASSISTANT_SPEC v1 §8,
  // functions/assistantMainThread.js): the GENERAL conversation — the same
  // thread the sidebar shows — over the home grid area with the main
  // placement; the reasoning pill and the suggestion island step aside while
  // it is up. The `_db_*` keys stay declared for the retired thread's rows.
  // Since 2026-09-29 (§12.2, main thread = "Everywhere") the flag holds on
  // every route: `_mainThreadPath` is the route it opened on — a navigation
  // closes the thread (functions/assistantMainThread.js assistantMainThreadSync).
  _homeThreadOpen: false,
  _mainThreadPath: null,
  // A new answer's section at the top of its view (functions/assistantReveal.js,
  // Nika 2026-10-23): the room the open main-area view lacks below that section
  // so the scroller can lift it there — `{ view, px }`, the view's place in the
  // tree and its `min-height`. One view holds the main area at a time, so one
  // room; the view gives it back when its conversation or session changes.
  _revealRoom: null,

  // ── Project Version History (2026-08-07 per Nika — legacy platform's
  // Versions rail rebuilt on the canvas page) ──
  // Visibility of the VersionHistoryPanel right rail. May be seeded true
  // BEFORE navigating to the canvas route (dashboard card menu → "Version
  // history"); the panel's onRender self-heals the fetch.
  _versionsOpen: false,
  // The single carrier for the whole history UI (the `_publish` idiom):
  // { projectId, branch, items, page, total, pages, hasMore, loading, error,
  //   selectedId, detail, detailLoading, restoreBusy, restoreError,
  //   restoredTo } — written ONLY by canvas/functions/versionHistory.js.
  _versions: null,

  // ── Site builder (/preview in the shell) ──
  // The website the builder shows, written by the builder page once its data
  // lands (preview/functions/previewSite.js): `{ owner, key, projectId, env,
  // page }`. The assistant resolves the site on screen PER SEND from the
  // visible route or pane (previewSiteInView) and takes `projectId` / `page`
  // from here only when this record names the same site. Null = no builder.
  _previewSite: null,
  // The builder's quiet status line: null, 'setup' (a new site is being
  // made — the builder waits for the first project write) or 'updating' (an
  // AI write landed; the frame reloads the latest saved version).
  _previewSiteStatus: null,
  // The running project write's own label (the server's tool label), shown
  // as the builder's non-blocking chip while the write runs. Null = none.
  _previewSiteStep: null,
  // True when the version on screen is a draft: the latest saved version is
  // not the published one. Drives the quiet "Draft · not published" mark.
  _previewSiteDraft: false,
  // The page the builder shows (`'/'`, `'/about'`): its page picker
  // (previewSiteGoTo) and its sidebar write it; the assistant is told about
  // it per send (previewSiteInView). Preview's own state.js declares the
  // same key for preview standalone. Null = the site's home.
  activePage: null,
  // The last AI project write the shell saw (`{ projectId, tool, at }`). The
  // canvas editor's update watcher (canvas pages/main.js) applies it to the
  // open project; null = none yet.
  _aiProjectWrite: null,

  // Reactive bridge for canvas v2 selection (2026-08-08 per Nika — the
  // CanvasPromptTextarea selection-chips row). `root.canvas.selection` is
  // mutated IN PLACE by functions/canvas/selection.js's setSel — never
  // through root.update — so nothing subscribeTo'd would ever re-render off a
  // selection change alone. setSel bumps this counter via
  // `root.update({ _cvSelectionTick: ... }, { preventFetch: true })` on every
  // selectOne/toggleSelection/selectMany/clearSelection; declarative
  // consumers `subscribeTo: ['_cvSelectionTick']` to re-read the live
  // selection themselves. Declared here (not left to smbls 3.14's
  // create-signal-on-first-write) per house convention — same as every other
  // canvas UI-signal key on this page.
  _cvSelectionTick: 0,

  _db_chatMessages: [],
  _db_chatTyping: false,
  _db_currentAssistantId: null,
  _db_activeProcesses: [],
  _db_runHistory: [],
  _db_sessions: {},
  _db_activeSessionId: null,
  _db_showSessionMenu: false,

  target: null,
  targetSelected: true, // skip the chrome-extension target-picker modal
  targetOptions: null,
  targetDraftMode: null,
  targetDraftCwd: null,
  targetDraftCustom: '',
  // sdk auth + cloud workspace state simone reads for its TargetPicker.
  // We don't mount the picker, but the components reference these on
  // create — keep them declared with safe defaults to avoid runtime
  // ReferenceError-style warnings from smbls' undeclared-write guard.
  sdkSignedIn: false,
  cliAuth: null,
  cloudWorkspaces: [],
  cloudWorkspacesLoading: false,
  cloudWorkspacesError: null,
  recentPairings: {},
  // Empty seed, read lazily: its one reader (functions/_simone/wsBridge.js)
  // falls back to `window.location.origin` when it runs — never at module
  // load (see `currentPath` above).
  currentOrigin: '',

  // ── canonical Chat header (hoisted into simone Chat.js) ──
  // The AssistantDrawer mounts simone's Chat wholesale; these keys back the
  // header's TargetSwitcher / ProviderDropdown / Avatar. Declared with safe
  // defaults so smbls 3.14 doesn't drop the writes.
  // Workspace-embedded: default to the workspace target (the my-app is always
  // workspace-scoped via sdk.ai) and hide 'General Questions' in the
  // TargetSwitcher — only 'Workspace' (default) + 'Local' are offered.
  targetMode: 'workspace', // default target (settings TargetSection label)
  noGeneralTarget: true, // hide 'General Questions' in the settings TargetSection
  targetPickerOpen: false, // picker not mounted in workspace; writes no-op
  targetPickerView: 'home',
  modelMode: 'auto', // forced model mode (body.modelMode), default auto
  _modelMenuOpen: false, // ModelDropdown gate (distinct from the home
  // ParamsBtn's _aiModePickerOpen)
  _aiPinMenuOpen: false, // AiSettingsPanel workspace-pin picker gate
  // Nullify simone's login gate — the workspace shell already owns auth.
  authBypass: true,
  authReady: true,
  authUser: null, // header Avatar hides while null
  inspectorTools: false, // hide composer Inspect $0 (not an inspector)
  hidePickTool: false, // composer Pick SHOWS in the drawer — picking
  // here just points the AI at an item (context),
  // not a DOM-update request like the extension
  workspaceEmbed: true, // drawer posture: hide the header Me avatar
  // (shell chrome shows one), pointing Pick copy,
  // embed-trimmed SettingsDrawer

  // ── Notifications ──
  // `notifications` = transient toast queue (GlobalNotification, shared method).
  // `notifs` = the persisted notification CENTER (bell + panel) — a SEPARATE key
  // so center rows never render as toasts or inflate the bell count.
  notifications: [],
  notifs: [],
  // The notification centre has answered at least once (functions/
  // notifications/subscribeNotifications.js fetchNotifications, a failure
  // too): Home's inbox signal waits for it (functions/homeSignals.js).
  notifsLoaded: false,
  notificationsPanelOpen: false,
  // NotificationPanel's All/Unread segmented filter. MUST be declared or the
  // tab clicks' `root.update({ _notifFilter })` is silently dropped (v3.14)
  // and the tabs never switch — shipped broken exactly that way (2026-08-01).
  _notifFilter: 'all',

  // HeaderTabs (module navbar) reorder-drag UI state. `_navbarReorder` is the
  // long-press arm flag (was undeclared since 2026-07-30 — its writes were
  // silently dropped, the same v3.14 trap as _notifFilter above); the three
  // drag keys drive the lift + insertion-indicator visuals (2026-08-01).
  _navbarReorder: false,
  _navbarDragPath: null,
  _navbarDragOverPath: null,
  _navbarDragOverAfter: false,

  // Reminders composer extras (2026-08-01): the armed @mention autocomplete
  // query (null = menu closed). The shared/private toggle default
  // `_reminderDraftShared` lives with its draft siblings further up — a
  // duplicate key here shadowed it (esbuild duplicate-object-key warning on
  // every workspace-shell platform push).
  _reminderMentionQuery: null,

  // ── Public user profile (`/user/:slug`) ──
  // Populated by pages/userProfile.js#onCreate. Without these declared,
  // smbls 3.14 silently drops the writes and the profile renders blank.
  _profileSlug: null,
  _profileViewed: null,
  _profileLoading: false,
  _profileTab: 'projects',
  _profileActivity: {},
  _profileProjects: null,
  _profileEditing: false,
  _profileSaving: false,
  _profileSaveError: null,

  // ── Follow graph ──
  // List of lowercase email addresses the current user follows.
  // Mirrored across marketplace + shell via localStorage hydrate.
  followingEmails: [],

  // ── Marketplace community feed (mounted at /marketplace/feed) ──
  // Each post mirrors the shape persisted by marketplace's community.js.
  feedFilter: 'all',
  feedPosts: null,
  feedLoading: false,
  composerOpen: false,
  composerDraft: { projectId: null, message: '' },
  composerSubmitting: false,
  composerError: null,
  _followInFlight: {},
  _myProjects: null,
  _openThreads: {},
  _commentsByPost: {},
  _commentsLoading: {},
  _commentDrafts: {},

  // ── Workspace presets (App Store presets row + sheet, new-account wizard) ──
  // The ONE writer is marketplace/functions/workspacePresets.js, which the shell
  // registers at boot (marketplace functions/eagerSlice.js). Declared so each
  // key exists from the first render (an absent key registers no dependency):
  //   `_workspacePresets` — null until the first good read of the public
  //     registry, then the preset list. `_workspacePresetsAt` — ms of that
  //     read (0 = never). `_workspacePresetsUnsupported` — this host's SDK has
  //     no `listWorkspacePresets`, so every preset surface hides itself.
  //     `_workspacePresetsError` — null | 'failed'. All four are the PUBLIC
  //     registry: unscoped (state.js, with the reason).
  //   `_presetApply` — null | { key, workspaceId, status, lines, result, error }:
  //     the apply in flight or just finished against ONE workspace. Scoped
  //     (WORKSPACE_SCOPED_KEYS below): a switch must not show workspace A's
  //     progress lines under workspace B.
  _workspacePresets: null,
  _workspacePresetsAt: 0,
  _workspacePresetsUnsupported: false,
  _workspacePresetsError: null,
  _presetApply: null
}

// ── Workspace-scoped keys ───────────────────────────────────────────────
// Every key below is a WORKSPACE-PROJECT-tier value (see state.js for the
// USER/ORG/WORKSPACE/PROJECT scope-cascade tiers) that MUST reset to its
// schema-empty value when the active org/workspace changes — a key left off
// this set silently BLEEDS the previous workspace's data into the next one
// on switch (the leak class the chat + aiChat isolation work fixed;
// tickets/workspace.md "declarative WORKSPACE_SCOPED_KEYS registry").
//
// Declared HERE, immediately next to the defaults above, grouped by the
// SAME section comments (people/teams, Tickets, Chat, aiChat, Calendar/
// Meet) — adding a new per-workspace feature's key is a one-line addition
// to whichever list already sits beside that key's default, not a hunt
// through onWorkspaceChange's function body.
//
// Consumed by state.js, which UNIONS this set with the ORG/WORKSPACE/
// PROJECT-tier keys it declares directly (their defaults live there, not
// here — state-app.js can't import state.js, which imports THIS file), and
// by scopes.js's workspaceScopedWipe(), which loops the combined set and
// resets each key to its state.js effective default (falling back to
// WORKSPACE_SCOPED_OVERRIDES below for the few keys whose switch-reset
// value isn't simply "this key's cold-boot default" — see loadPrivateData.js
// onWorkspaceChange PHASE 1).
//
// UI-only inputs (searchQuery, filterAssignee, filterLabel, peopleQuery,
// docsQuery, kbQuery, …) are INTENTIONALLY absent — they carry across a
// switch via onWorkspaceChange's `...root.parse()` spread, same as today.
export const WORKSPACE_SCOPED_KEYS = new Set([
  '_workspaceActivation',
  // people / teams
  'peopleAll',
  'people',
  // FALSE-ZERO-HOME-NON-AGENT-KEYS-1 — the roster's load marker + error key.
  // Both are per-workspace facts: the next workspace's roster is unread until
  // its own producer runs, so carrying `peopleLoaded: true` across a switch
  // would let the new workspace render the previous one's headcount as a
  // verified number.
  'peopleLoaded',
  'peopleError',
  'teams',
  'teamsByEmail',
  'teamsByUserId',
  'teamMembersLoaded',
  'orgMembersByOrgId',
  // WS-SWITCH-INCOMPLETE-1 (2026-08-20): the org MEMBER-ROLE MAP left quarantine
  // here, beside `orgMembersByOrgId` / `peopleAll` — the same
  // `sdk.listOrgMembers(jwtClaims.workspace_org_id)` payload, split across two
  // keys of which only one was registered. `roles.byUser` maps a GLOBAL userId to
  // that member's role in the ACTIVE org, and `roles.byUserMemberId` maps it to
  // the OrgMember._id that `sdk.updateOrgMember` PATCHes.
  // ⭐ THE LEAK IS THIS FILE'S OWN KNOWN BUG, HALF-FIXED. loadPrivateData's
  // switch tail already records it: "role badges kept showing the PREVIOUS org's
  // member-role mapping (`root.roles.byUser` is keyed by the global userId, not
  // scoped per-org, and nothing re-ran the fetch)". That fix added the REFETCH
  // and never added the WIPE — and `_loadRoles` fires in the NON-BLOCKING TAIL,
  // AFTER reveal, behind a network round trip. So for the whole duration of that
  // round trip /people, /admin, PersonCard, MemberProfile and availabilityUtils
  // render workspace A's roles against workspace B's roster, on revealed UI. A
  // global-id key is the shape that leaks silently: the same userId is a valid
  // key in both tenants, so a stale entry never looks wrong.
  // PHASE-2 pair: already present and UNCONDITIONAL — `_loadRoles(root, sdk)` in
  // the switch tail's `void Promise.all([...])`. It carries NO "already fetched"
  // latch, module-level or element-local; its only gates read ROOT state
  // (`jwtClaims.workspace_org_id`, preserved by PHASE-1's `...root.parse()`, and
  // `peopleAll`, filled by the awaited essential set before the tail runs). ⇒
  // nothing outside root state can strand this key.
  // ⛔ Wipes to `null` via WORKSPACE_SCOPED_OVERRIDES, NOT to a bare `{}` — see
  // the override entry for why a bare `{}` would break two shape fallbacks.
  'roles',
  'pendingInvitations',
  // WS-SWITCH-INCOMPLETE-1 (2026-08-19): the four /admin/teams INVITE DRAFTS left
  // quarantine here, beside the `pendingInvitations` rows they produce — the
  // "headline registered, satellite left behind" shape: the fetched invite list was
  // scoped, the drafts that create it were not. An invite draft belongs to the
  // workspace you are inviting INTO. ⭐ `_wsInviteTeams` makes the leak concrete
  // rather than cosmetic: it holds TEAM SLUGS of the current workspace
  // (pages/admin/teams.js team toggles) and the submit posts them together with the
  // ACTIVE `workspace.id` to `inviteToWorkspace` — so a draft that survives a switch
  // writes workspace A's team slugs into workspace B's invite. Safe to wipe with no
  // PHASE-2 pair: a draft's correct post-switch value IS its schema default, and no
  // module-level memo guards them (every read is a direct `s.root?.…` in the page).
  // ⛔ `_wsInviteRole` resets to 'editor', NOT '' — _freshEmpty passes scalars
  // through, so the role select keeps a valid option. Do not "fix" that with a
  // WORKSPACE_SCOPED_OVERRIDES entry.
  '_wsInviteEmail',
  '_wsInviteRole',
  '_wsInviteTeams',
  '_teamInviteEmail',
  // 🚨 WS-SWITCH-INCOMPLETE-1 (2026-08-19): the invite RESULTS, moved here from
  // WORKSPACE_UNSCOPED_KEYS — a MISCLASSIFICATION, not a quarantine drain. They
  // sat under the comment "TRANSPORT flags — in-flight/loading/typing booleans,
  // re-derived by the switch itself", and BOTH HALVES OF THAT WERE FALSE, which
  // is exactly why the leak survived review:
  //   • Not booleans — objects `{tone, message, inviteUrl}`. `inviteUrl` is the
  //     server's invite link for the ACTIVE workspace (`res?.inviteUrl` from
  //     inviteToWorkspace), which pages/admin/teams.js RENDERS as visible text
  //     beside a copy button that writes it to the clipboard. `message` also
  //     embeds the invitee's email and the selected team names.
  //   • Nothing re-derived them on a switch — their only writers are the invite
  //     submit handlers, and the two `: null` resets are modal-close paths, not
  //     switch paths. Workspace A's invite URL stayed on screen, and copyable,
  //     after switching to workspace B.
  // Wipes to `null`, which is both the schema default and the correct
  // post-switch value (no invite is in progress in the new workspace), so no
  // PHASE-2 refetch pair is needed.
  // ⚠️ ONE NARROWER WINDOW REMAINS, deliberately not fixed here: the success
  // write lands AFTER `await inviteToWorkspace(...)`, so a switch DURING an
  // in-flight invite still writes workspace A's result into workspace B. That
  // needs a scope guard at the handler in pages/admin/teams.js — a different
  // file and a different argument. Scoping closes the persistent case, which is
  // the one a user actually hits.
  '_wsInviteStatus',
  '_teamInviteStatus',
  'announcements',
  'resourceLinks',
  'userDocs',
  'userPrefs',
  'orgNotifCount',
  // WS-SWITCH-INCOMPLETE-1 (2026-08-20): the per-type signals join their headline
  // here. `fetchOrgNotifCount` writes BOTH keys in ONE
  // `root.update({ orgNotifCount, orgNotifSignals })`, and only the headline was
  // ever registered — the "satellite left behind" shape, provable by CO-WRITE
  // rather than by name. Without this the two halves of one payload disagree
  // after every switch: the count empties, the signals keep the pre-switch
  // numbers. Wiping is free today (the repo-wide scan finds ZERO readers — four
  // hits total, all declaration/comment/write), and the write's own comment
  // promises "a future per-type surfacing can read root.orgNotifSignals[orgId]
  // without refetching" — that future reader would show stale counts on revealed
  // UI and would look correct, because the badge beside it is right.
  // PHASE-2 pair: already present and UNCONDITIONAL — `fetchOrgNotifCount(root)`
  // in the switch tail. No "already fetched" latch, module-level or
  // element-local; its two early returns (`document.hidden`, missing auth token)
  // are a DOM fact and a transport fact, not memos, and the hidden-tab case
  // self-heals on the next NOTIF_POLL_MS tick of app.js's poll interval — the
  // same gate `orgNotifCount` has always been scoped through.
  // ⛔ SCOUTED, NOT ANSWERED HERE: app.js's poll comment calls this pair
  // "Cross-org by design — not reset on workspace switch", which the headline's
  // own scoped classification already contradicts. Whether the PAIR belongs in
  // USER scope is a separate argued diff; a satellite drain must not silently
  // reclassify its headline, and splitting one `root.update` across opposite
  // registries would be worse than either consistent answer.
  'orgNotifSignals',
  // Tickets — board cache + /tickets/my + /tickets/feed
  'tickets',
  'myTickets',
  // FALSE-ZERO-HOME-NON-AGENT-KEYS-1 — Home's own ticket cache and its
  // load-state pair (see their declarations above). Same tenancy boundary as
  // the board cache they sit beside; the one-shot gate MUST reset on a switch
  // or the new workspace never fetches its own tickets.
  '_userTickets',
  '_homeTicketsLoaded',
  '_homeTicketsError',
  // Workspace presence rollup for the Home analytics widget — scoped
  // `projectId: 'workspace'` per PRESENCE-ZERO-1, so it is the ACTIVE
  // workspace's number and must not survive a switch.
  '_homeWorkspaceActivity',
  '_homeWorkspaceActivityError',
  'feedItems',
  'totalByColumn',
  // PR #283's sidebar TYPE counts: the SAME workspace's tickets as
  // totalByColumn, written in the same root.update. Kept across a switch they
  // would show the previous workspace's numbers beside the new board; the
  // board's `workspace` watcher re-runs the load wave, which refills them.
  'totalByType',
  // Their raw source (PR #287): the same workspace's counts, refilled by the
  // same load wave; kept across a switch they would re-sum A's numbers on B.
  '_rawTypeCounts',
  'loadedByColumn',
  'loadingColumn',
  'columns',
  'selectedTicket',
  // The board's ?project= scope: a project id from THIS workspace, which
  // every column query filters by (filtersFromRoot → _buildMongoFilter). Kept
  // across a switch, the next workspace's board would filter by a project it
  // does not have and show empty columns.
  'filterProjectId',
  // WS-SWITCH-INCOMPLETE-1 step 4 (2026-08-20) — the ticket comment thread left
  // quarantine here. It holds comment ROWS (body, author email, timestamp) for
  // the open ticket, so it is tenant data under any reading, and it is exactly
  // the "headline registered, satellite left behind" shape this ticket names:
  // `selectedTicket` directly above was registered, the thread hanging off it
  // was not, so a switch wiped the ticket and kept its comments.
  //
  // Quarantined until now because the comments plane carried a permanent
  // "ever fetched" memo. That plane was REMOVED, not keyed (`workspace
  // aeec537df`) — `_commentsInFlight` releases in a `finally` — so the PHASE-2
  // refill is just the display reactive re-firing on the next ticket open.
  // `_ticketCommentsFor` is the slot's own tag and MUST wipe with it; see its
  // declaration above for why it stopped being an element property.
  '_ticketComments',
  '_ticketCommentsFor',
  '_ticketCommentsSkip',
  '_ticketCommentsHasMore',
  '_ticketCommentsLoadingOlder',
  '_ticketCommentsLoading',
  '_editingCommentId',
  '_commentEditDraft',
  // WS-SWITCH-INCOMPLETE-1 step 5 (2026-08-20) — the dependency plane left
  // quarantine here. `_ticketDeps` caches the open ticket's dependency ROWS
  // (id + title of tickets on both sides of the link) keyed by ticket id, and
  // `_depPool` holds the 200 most-recent tickets of the ACTIVE workspace —
  // the list the "Add dependency" picker searches. Neither was wiped, so after
  // A -> B the picker searched workspace A's titles and picking one wrote a
  // dependency across a tenant boundary. `selectedTicket` and `tickets` beside
  // them were already registered: the same "headline registered, satellite
  // left behind" shape.
  //
  // The PHASE-2 refill needs no new teardown in either case, because both
  // guards read the wiped state itself: `_ticketDeps`'s per-ticket fetch is
  // gated on `cache[id]` and the pool's on `!_depPool && !_depPoolError`. The
  // two flags MUST wipe with the pool — a mid-switch failure or an unreleased
  // in-flight flag is itself a permanent "do not retry" signal for the next
  // tenant.
  '_ticketDeps',
  '_depPool',
  '_depPoolLoading',
  '_depPoolError',
  // WS-SWITCH-INCOMPLETE-1 step 3, bucket A2 (2026-08-20) — THE APPROVAL
  // DOMAIN, all five keys of it, and the only place on this ticket where a
  // leftover value REMOVES a control instead of showing a stale one.
  //
  // ⭐ THE DEFECT. The human-approval bar has two gates and only one of them
  // reads the ticket:
  //   ApprovalBar  → `_isAwaitingApproval(root) && !_isEditing(root) &&
  //                   !root._approvalDoneAt`
  //   ApprovedPill → `root._approvalDoneAt && !_isEditing(root)`
  // `_isAwaitingApproval` reads `selectedTicket` (registered above), so the
  // first gate follows the tenant. The pill's gate does not read the ticket at
  // all, and NOTHING ever clears the pair — two writers (the approve and the
  // reject success paths), zero resets. So one decision in workspace A hides
  // the Approve/Reject buttons on every ticket opened afterwards in workspace
  // B and renders `APPROVED · 14:32` over them instead. The user cannot
  // approve, and is told the decision is already made.
  //
  // ⭐ `_approvalNote` IS THE SINK RULE AGAIN (see `_reminderDraft` below).
  // Traced backward it is free text the user typed, which step 6's
  // overlay-pointer rule files UNSCOPED. Traced FORWARD it is the `body` of a
  // `ticket_comment` INSERT — `_approveTicket` hands it to
  // `comments.create(t.id, body)`. Carried across, a note written about
  // workspace A's ticket becomes a permanent audit-trail row on workspace B's
  // ticket under the current user's email. A draft's sink is a WRITE.
  // ⛔ Its empty is `''`, its declared default — not null. The textarea binds
  // `value` directly.
  //
  // ⭐ `_approvalError` IS TENANT-DERIVED ON ONE BRANCH, WHICH IS ENOUGH. Two
  // sources write it: a fixed copy string ("Session not ready…") and
  // `err?.message` from an SDK call issued against workspace A's ticket. A key
  // is classified by its WORST source. Its 4-second auto-clear timer is not a
  // reset either — it fires late and unconditionally, so unguarded it reaches
  // across the boundary and clears an error workspace B raised for itself.
  //
  // ⭐ AND THE RULE THIS DOMAIN ADDS — A TENANT GUARD CAN ORPHAN A KEY THAT
  // WAS CORRECTLY CLASSIFIED BEFORE IT. `_approvalPending` sat under state.js's
  // TRANSPORT FLAGS heading, whose claim is "re-derived by the switch itself".
  // That claim held only by accident: nothing re-derives it, but the completion
  // handler always ran and always wrote `false`. This commit adds
  // `sameWorkspaceWriter` to those handlers — mandatory, because an approval
  // issued in A otherwise lands its `_approvalDoneAt` / `_ticketComments` /
  // `selectedTicket` writes in B AFTER the wipe. The instant the guard exists
  // the completion can be DROPPED, and a dropped completion never clears the
  // latch: workspace B's Approve button then reads `Approving…` and `disabled`
  // forever. ⇒ The guard and this reclassification MUST ship in one diff;
  // adding the guard alone converts a leak into a wedge.
  // ⛔ It wipes to `false`, never null — `false` is the declaration, and the
  // partition asserts declaration equality, not truthiness.
  '_approvalError',
  '_approvalNote',
  '_approvalPending',
  '_approvalDoneAt',
  '_approvalKind',
  // Chat — RLS-scoped by workspace_id
  // WS-SWITCH-INCOMPLETE-1 (2026-08-19): `_pinnedChannels`/`_hiddenChannels` left
  // quarantine here. ⭐ Their localStorage IS already tenant-segmented
  // (`chat_pinned_channels:${wsId}`, channelActions.js:259) — but a correctly
  // partitioned STORE does not help if its in-memory MIRROR is never reset: the
  // store held workspace B's pins while state still showed A's. Safe to wipe
  // because the PHASE-2 re-read already exists — `loadPinnedChannels` re-reads on
  // mount (MessengerOverlay.js:146, chat/pages/index.js:143) — and no module-level
  // memo guards it (`_loadPinned` hits localStorage every call).
  '_pinnedChannels',
  '_hiddenChannels',
  'channels',
  'channelMembers',
  'messagesByChannel',
  'myMentions',
  'activeChannelId',
  'activeThreadRootId',
  'threadOpen',
  'threadParentId',
  'composerReplyTo',
  '_editingMsgId',
  '_editingMsgBody',
  'typingByChannel',
  'chatVisibleCounts',
  '_chatDrawerChannels',
  '_chatDrawerChannelId',
  '_chatDrawerMessages',
  // The pill's away mark and each channel's unread span hold this tenant's
  // channel ids and message times; the open emoji picker and the thread
  // field's has-text flag belong to composers on the page the switch leaves
  // (the `_aiInspect` precedent: dropped with the page they were over). Each
  // wipes to a value its writers already produce: null, null, {} (the
  // MessageList prune writes it), null, false.
  '_chatAwaySince',
  '_chatAwayCid',
  '_chatNewFrom',
  '_composerEmoji',
  '_threadHasText',
  // Who is typing in which channel — another workspace's channels and
  // people; wipes to {} (what the chat stream's prune writes).
  '_chatTypists',
  // WS-SWITCH-INCOMPLETE-1 step 3, bucket A2 (2026-08-21) — THE CHAT DRAFTS.
  //
  // ⭐ `newChannelDraft` IS THE SINK RULE (see `_reminderDraft` below) WITH
  // TENANT ROWS ALREADY INSIDE IT. Backward it is a form the user typed
  // (name, topic, kind). Forward it is the payload of `createChannel`:
  // `d.members` is a list of EMAILS picked from workspace A's roster and
  // `createChannel` turns every one of them into a `chat_channel_members`
  // INSERT. Carried across, a channel started in A and finished in B seeds
  // B's new channel with A's people. The draft becomes rows.
  // ⛔ Wipes to `null`, NOT to the `{name:'',…}` default and NOT to `{}`. The
  // rule: A WIPE MUST PRODUCE A VALUE SOME WRITER ALREADY PRODUCES, so every
  // reader has met it. `closeNewChannelModal` writes `newChannelDraft: null`;
  // `{}` is a shape no writer produces. Every read is
  // `s.root?.newChannelDraft?.X` or `|| {…}`, and `openNewChannelModal`
  // re-seeds the full shape on open. (WORKSPACE_SCOPED_OVERRIDES below.)
  //
  // ⭐ `pendingAttachments` IS KEYED BY CHANNEL ID — a map from workspace A's
  // channel ids to upload chips (name, size, mime, the public URL once
  // uploaded, a `blob:` preview and the File itself). Left in place, A's
  // channel ids sit in B's root as keys and A's file URLs sit under them. It
  // is tenant data by content and by key.
  // ⛔ Wipes to `{}` (the declared default, via `_freshEmpty`), NEVER null:
  // Composer's AttachmentBar `children` reads
  // `s.root.pendingAttachments[s.root.activeChannelId]` UNGUARDED, and so does
  // `uploadAttachment`. `{}` is also writer-produced — `sendMessage` deletes
  // the channel's entry after a send.
  // ⚠️ Known, not fixed here: a chip's `blob:` object URL is revoked only by
  // the chip-remove / retry / send paths (chat/pages/index.js, sendMessage.js).
  // The wipe drops the chip without revoking, so a pending chip that crosses
  // a switch keeps its object URL alive until page unload. A registry wipe
  // cannot run per-key teardown; that wants a switch hook this registry does
  // not have. Recorded on the ticket.
  // ⚠️ Also known: `uploadAttachment`'s two post-await writes (success and
  // failure) are NOT tenant-guarded — after a switch they land
  // `{ [A-channelId]: [] }` in B's root (the wiped list, mapped, is empty).
  // The chat package has NO `sameWorkspaceWriter` today, and its loaders of
  // already-scoped keys (`channels`, `messagesByChannel`) have the same late
  // write exposed with far more payload — one ticket for the class, not a
  // fourth copy of the helper for one key: CHAT-LATE-WRITES-NO-TENANT-GUARD-1.
  'newChannelDraft',
  'pendingAttachments',
  // AI assistant threads — WORKSPACE-scoped (2026-07-14 per Nika)
  '_aiThreads',
  '_aiThreadId',
  '_aiThreadsLoading',
  '_aiThreadsLoadAttempted',
  '_aiThreadMenuOpen',
  'assistantMessages',
  'assistantTyping',
  // Calendar / Meet page caches — RLS-scoped by workspace_id
  'events',
  // WS-SWITCH-INCOMPLETE-1 step 3, bucket A2 (2026-08-21) — `newBooking`, THE
  // LAST KEY OF THE BUCKET, drained last on purpose: it was the survivor
  // control of every live probe on the ticket until the rest was proven.
  //
  // ⭐ SINK RULE, AND THE `_broadcastDraft` SHAPE OF IT. Backward it is the
  // booking form. Forward it is `saveEvent`'s payload into the ACTIVE
  // workspace — and the save BRANCHES ON A ROW ID THE DRAFT CARRIES:
  // `newBooking.editingEventId` is workspace A's calendar_events id, and the
  // off-route edit path PATCHes it directly (`saveEvent({ id:
  // nb.editingEventId, ...payload })`). A Save pressed in B after a switch is
  // a CROSS-TENANT WRITE against A's event; the header even reads "Edit
  // Booking" over B. `invitees` is A's roster emails (each becomes an invite
  // + notification on save) and the Delete control gates on the same id.
  // ⛔ Wipes to `{}` (the declared default, via `_freshEmpty`), NO override.
  // This key has no close value to borrow — `closeNewBookingModal` clears
  // `showNewBooking` only and never nulls the draft — and no writer produces
  // `null` either; the two modal drafts above could borrow theirs, this one
  // cannot. `{}` is the closer of the two non-writer values: every reader is
  // `root?.newBooking?.X` or spreads it (`{ ...root.newBooking, invitees }`,
  // which `{}` and `null` both survive), `nb?.date` falsy short-circuits the
  // availability check, and EVERY opener re-seeds `freshBooking(overrides)`
  // before the modal is usable (`openNewBookingModal`); submit resets with
  // `freshBooking({ date })` too. The per-switch loss is half-typed text,
  // which is the desired behaviour for a tenant boundary.
  'newBooking',
  // Reminders — personal records-plane rows, tenant-scoped (functions/
  // reminders.js). Filter/draft UI keys stay OFF this list (see their
  // declarations above) — only the fetched row cache resets on switch.
  'reminders',
  // FALSE-ZERO-HOME-NON-AGENT-KEYS-1 — the reminders load marker + error key,
  // on the same tenancy boundary as the row cache they describe.
  'remindersLoaded',
  'remindersError',
  // History — the audit-log rows of one workspace, their load marker and
  // error key (the page reads on mount; the switch tail re-reads while it is
  // on screen — loadPrivateData.js `historySurfaceActive`), the person filter
  // (an email of THIS roster) and the open-card map (its keys carry those
  // emails).
  'historyEvents',
  'historyLoaded',
  'historyLoadError',
  'historyPerson',
  '_historyExpanded',
  // Getting started → Team (POB S9) — the found people of one workspace, the
  // owner's typed addresses for them, the domain switch of that workspace and
  // the page's send state (the page reads on mount; the switch tail re-reads
  // while it is on screen — loadPrivateData.js `gettingStartedSurfaceActive`).
  'gettingStartedTeamStatus',
  'gettingStartedPeople',
  'gettingStartedDomain',
  'gettingStartedInvites',
  'gettingStartedOther',
  'gettingStartedSending',
  'gettingStartedSendResult',
  'gettingStartedDomainJoin',
  'gettingStartedDomainJoinStatus',
  '_gettingStartedWhyOpen',
  // Getting started → Imports (POB S10) — the found sources of one
  // workspace, the open review of one of its files and the run into one of
  // its apps (the page reads on mount; the switch tail re-reads while it is
  // on screen — loadPrivateData.js `gettingStartedImportsActive`).
  'gettingStartedImportStatus',
  'gettingStartedSources',
  'gettingStartedTargets',
  'gettingStartedReview',
  'gettingStartedMapping',
  'gettingStartedImportError',
  'gettingStartedUploading',
  'gettingStartedRun',
  'rooms',
  'waitingRoom',
  // Meet realtime + transcript surfaces (2026-07-30 isolation audit #7):
  // written by subscribeMeetRealtime/TranscriptPage, previously carried
  // meeting content across tenants indefinitely.
  'utterances',
  'analysis',
  // per-room with `analysis`: a switch must not carry room A's "loaded"
  // over room B's transcript
  'transcriptLoaded',
  'liveParticipants',
  'activeRoomId',
  // a ring is a room of THIS workspace: a switch must not carry it over
  '_callRing',
  // the timeline of THIS workspace: a switch must not carry it over
  '_callChanges',
  // WS-SWITCH-INCOMPLETE-1 step 3, bucket A2 (2026-08-21) — THE MEET DRAFT
  // AND THE CALL ERROR.
  //
  // ⭐ `newRoomDraft` — SINK RULE. Backward: name / kind / transcribeEnabled /
  // isPrivate, no tenant id in it. Forward: it is the payload of `createRoom`,
  // a `rooms` INSERT into the ACTIVE workspace — a call named in A is created
  // in B. Wipes to `null`, the value `closeNewRoomModal` writes; every reader
  // goes through `_draft(s)` = `root.newRoomDraft || {}`, and EVERY opener
  // re-seeds the full shape (`openNewRoomModal`, meet/pages onCreate,
  // MainNavbar's Meet button). Same rule as `newChannelDraft` above.
  //
  // ⭐ `meetCallError` (+ `meetCallErrorName` / `meetCallErrorCode` / — added
  // leg 14 — `meetCallErrorReason` / `meetCallErrorStatus`, its four
  // satellites, each UNDECLARED until the diff that scoped it) — the
  // `_approvalError` argument:
  // `err?.message` from a connect issued against workspace A's room, and a
  // key is classified by its WORST source. Left in place, VideoRoom's
  // ErrorOverlay (`hide: !s.root?.meetCallError`) shows A's "Couldn't join: …"
  // over B's room until B dismisses it. The three are written together in
  // both writers (connectCall's pre-flight reset and its catch), so they land
  // in one set (LOCKSTEP). All five wipe to `null`.
  // ⚠️ THE LOCKSTEP IS THE POINT, AND IT BROKE ONCE ALREADY. Step 3 scoped the
  // triple; a later diff widened the record to five and scoped neither new
  // key, so a switch wiped 3 of 5 and left A's `reasonName` + HTTP `status`
  // feeding VideoRoom's overlay copy under B's room — a PARTIAL wipe of one
  // co-written record, which reads worse than no wipe. The root-write census
  // (b0f12cb90) is what named the two by hand; the source pin in
  // meetChatDraftsTenantScope asserts the writer's FULL shape so the next
  // widening of this record fails the suite instead of leaking quietly.
  // ⭐ THE TENANT GUARD ON connectCall's COMPLETION EXISTS NOW, AND ONLY
  // BECAUSE THE SWITCH DISCONNECTS FIRST (MEET-LIVE-CALL-SURVIVES-SWITCH-1).
  // Step 3 withheld it on purpose: dropping the success write while the
  // LiveKit connection itself was live would have left an INVISIBLE active
  // call in B — a worse wedge than a stale banner. `onWorkspaceChange` now
  // el.calls `disconnectCall` BEFORE its PHASE-1 wipe, so a join that
  // completes after a switch is provably orphaned; connectCall's guarded
  // completion (`sameWorkspaceWriter`) therefore DISCONNECTS that room
  // instead of merely skipping the update. Order is the whole fix: teardown,
  // then wipe, then guard.
  'newRoomDraft',
  'meetCallError',
  'meetCallErrorName',
  'meetCallErrorCode',
  'meetCallErrorReason',
  'meetCallErrorStatus',
  // MEET-LIVE-CALL-SURVIVES-SWITCH-1 (2026-08-21) — THE LIVEKIT SESSION
  // FAMILY, released from quarantine as ONE LOCKSTEP GROUP with the teardown
  // above. These four MIRROR the module-level `_livekitRoom` singleton in
  // meet/functions/connectCall.js — state cannot hold the Room itself (the
  // reactive proxy strips its class methods), so the mirror is all the UI
  // reads. Two of them carry workspace-A content outright: participant
  // identities and free-text call chat.
  // ⛔ WHY THEY COULD NOT SHIP EARLIER, AND WHY THEY SHIP NOW. Wiping the
  // mirror while the room still publishes hides CallWidget over a LIVE call —
  // the wrong state, not a fix, which is why they sat in
  // WORKSPACE_SCOPE_UNREVIEWED through the whole WS-SWITCH-INCOMPLETE-1 drain.
  // The switch path now tears the room down first, so the wipe describes
  // reality. If the teardown is ever removed, these four go back to
  // quarantine in the same diff — they are one unit.
  // All four wipe to their declaration (`false`, `[]`, `[]`, `[]`); no
  // OVERRIDES entry is owed. `meetCallInFlight` stays UNSCOPED — it is a
  // latch, not tenant content, and connectCall's own guarded writers own it.
  'livekitConnected',
  'livekitParticipants',
  'livekitSpeakers',
  'meetChatMessages',
  // A call's fly page and its @-query are this workspace's call (meetFly.js).
  'meetFly',
  '_meetMention',
  // WS-ROOTWRITE-CENSUS-CHAT-ROOM-KEYS-UNDECLARED-1 (2026-10-18) — the rooms
  // sidebar's edit mode and its archived view: the `filesSelectedIds` class
  // below, per-surface UI selection that points at another workspace's rows.
  // `selectedRooms` holds workspace A's room ids and IS the payload of the bulk
  // Archive / Unarchive (meet functions/archiveRooms.js): carried into B, B's
  // Archive button would write A's rooms into the archive list.
  // `isEditingRooms` travels with it (LOCKSTEP — the Edit / Done toggle in
  // Sidebar.js and both archive writers write the pair together).
  // `viewingArchived` carried into B keeps B on the archived view while the
  // browser-global archive list still holds A's ids: it lists none of B's
  // rooms and hides the rest. All three wipe to their declaration (`false`,
  // `[]`, `false`), the values Done and both archive writers already write; no
  // OVERRIDES entry is owed. `archivedRoomsIds` itself is unscoped (state.js).
  'isEditingRooms',
  'selectedRooms',
  'viewingArchived',
  // Documents / KB / notes (audit #2 — CRITICAL: previous workspace's doc
  // titles rendered on the next workspace's dashboard DocumentsWidget).
  'documents',
  '_documentsLoaded',
  'notes',
  'kbArticles',
  // Files desktop (audit #2): nodes + docs + per-surface UI selection state
  // that points at foreign row ids after a switch.
  'fileNodes',
  '_fileNodesLoaded',
  // Workspace A's failed fetch is no news about B: a switch wipes it to
  // `null`, its declared default, and B's own fetch writes its outcome.
  '_fileNodesError',
  'fileDocs',
  '_fileNodePos',
  'filesOpenFolder',
  'filesSelectedIds',
  'filesViewer',
  // In-flight upload ghosts. An upload started in the previous workspace either
  // already landed there or is about to fail against the new claim — either way
  // its ghost must not sit on the next workspace's desktop. (uploadFilesToCanvas
  // removes each entry by key in its own `finally`, so a late removal against a
  // wiped array is a no-op filter, not a resurrection.)
  '_filesUploading',
  // Additive workspace apps (audit #1 — the crm-recent-in-GreenIG vector):
  // the stash feeds _allModuleWidgetsFor/homeCatalogFor, so a stale bag kept
  // the PREVIOUS workspace's app widgets in the next one's catalog. Wiped
  // here; loadWorkspaceApps repopulates in the activation chain.
  'workspaceApps',
  // The workspace MODULE bag itself (manifest: widgets/routes/skin/panels) —
  // per Nika 2026-08-01 "loading old apps … is super malicious": this key
  // was NEVER in the registry, so the PREVIOUS workspace's module widgets,
  // routes and skin stayed live in state (and therefore in the DOM catalog)
  // for the whole wipe→reload window. Wiped to the not-loaded sentinel;
  // loadWorkspaceProject repopulates unconditionally in the switch pipeline.
  'workspaceProject',
  // Home dashboard per-workspace visibility map — recomputed by the widget
  // resync from the new workspace's panels; carrying it over let one
  // workspace's hidden set mask another's dashboard.
  '_homeHiddenWidgets',
  // Home dashboard per-widget COLOUR map (2026-09-02, "Add color"). Scoped
  // for the same reason as the visibility map above, and more sharply: house
  // widget ids COLLIDE across workspaces by construction — every workspace
  // has `greeting`, `received`, `app-chat` — so carrying this over paints one
  // workspace's choices onto another's board. Its cache key already carries
  // the workspace scope (pages/main.js `_widgetColorsKey`); the switch
  // re-resolves it from the new workspace's row/cache.
  '_homeWidgetColors',
  // The open "Add color" swatch palette, `{ widgetId, x, y }`. Transient UI,
  // wiped because the widget it points at need not exist in the workspace
  // being switched to — a palette left open over a vanished card would write
  // a colour for a widget that is not on this board.
  '_homeColorPicker',
  // Home board LAYOUT v2 — the saved ORDER of widgets, one size each
  // (DESIGN-WL-HOME-WIDGETS-1, pages/main.js `_resolveHomeLayout`). Scoped
  // like the visibility map: ids collide across workspaces (`received`,
  // `app-chat`), so one workspace's order must never lay out another's board;
  // the board re-resolves it from the new workspace's row / scoped cache.
  '_homeLayout',
  // The widget being dragged in edit mode (its id, for the lifted look).
  // Transient UI, like the palette above: a drag cannot outlive a switch.
  '_homeDragId',
  // Org/company financial identity (audit runners-up).
  'companyInfo',
  'financials',
  // Analytics page state (audit #1b table).
  '_wsMembers',
  '_projectsForActiveOrg',
  // The app split's signals (AI-app ids, catalog ids) belong to ONE
  // workspace — a switch must not classify org B's projects with org A's ids.
  '_projectKindSignals',
  // Analytics dashboards + logs tables (2026-08-08 merge audit): every
  // one of these holds tenant-scoped analyzed rows / rollups. None were
  // in the registry, so a workspace switch carried org A's sessions,
  // logs and error clusters into org B's view until each surface
  // happened to refetch. The fetcher caches are workspace-keyed now;
  // the state slots get wiped here.
  '_analytics',
  '_analyticsSessions',
  '_analyticsActiveUsers',
  '_analyticsServerSource',
  '_analyticsSampleSize',
  '_analyticsProjectKey',
  '_analyticsParams',
  '_wsaWeekly',
  '_wsaBugs',
  '_wsaOpenTasks',
  '_wsaActive',
  'logsUsers',
  'logsSessions',
  'logsEvents',
  'logsNetwork',
  'logsVerbose',
  'logsBugs',
  'logsSessionDetail',
  'logsSessionEvents',
  '_logsSessionId',
  '_logsCountry',
  '_logsUser',
  // WS-ANALYTICS-FAMILY-SPLIT-UTC-TOTALS-1 — the overall totals, top pages
  // and top referrers feeds (analytics/functions/loadAnalyticsData.js), the
  // zone the `now` rollup was bucketed in, and the workspace overview's
  // 7-day totals: all tenant-scoped analyzed rollups, same class as the
  // block above.
  '_analyticsTotals',
  '_analyticsTotalsError',
  '_analyticsPages',
  '_analyticsReferrers',
  '_analyticsRollupTz',
  // WS-ANALYTICS-UTM-CAMPAIGNS-UI-1 — the UTM campaigns feed, the last-12-
  // days feed (both tenant-scoped analyzed reads) and the sessions-list
  // drill filters a campaign row / visitor row / session detail sets
  // (`_logsUtm*`, `_logsVisitor` — same class as `_logsCountry` /
  // `_logsUser`: carried into B they filter B's sessions by A's ids).
  // (The group-by pill `_analyticsCampaignsGroupBy` is a USER preference —
  // WORKSPACE_UNSCOPED_KEYS in state.js.)
  '_analyticsCampaigns',
  '_analyticsCampaignsError',
  '_analyticsDaily',
  '_analyticsDailyError',
  '_logsUtmSource',
  '_logsUtmMedium',
  '_logsUtmCampaign',
  '_logsVisitor',
  '_wsaTotals',
  '_wsaTotalsError',
  // Calendar team view + standups (audit #1b table).
  'teamMembers',
  // WS-CALENDAR-TEAMMATE-KEYS-UNCLASSIFIED-BREAK-SUITE-1 — `teamMembers`'
  // AddTeammatesModal satellites (PR #253). Same class as the standup
  // satellites below: `addTeammateFeedback` renders a teammate NAME from the
  // active org ("X is already in Team") and `addTeammateQuery` holds typed
  // member text, so carried into B they are tenant residue on screen; the
  // open flag rides along so a switch never leaves A's roster editor open
  // over B.
  'showAddTeammates',
  'addTeammateQuery',
  'addTeammateFeedback',
  '_dailyUpdates',
  '_standupSummary',
  '_standupActions',
  // WS-SWITCH-INCOMPLETE-1 step 3 (standup domain) — the rest of the plane.
  // `_standupSummary`/`_standupActions` were registered alone; the satellites
  // below survived every switch. Standups are per-workspace by route
  // (`/workspaces/:id/standups`), so all of it is tenant data.
  //   `_dailyDraft`   — the SINK rule: it is not display text, it BECOMES
  //     `update_text` on the row `_saveStandup` upserts into whichever
  //     workspace is ACTIVE at save time. Carried into B it is a
  //     cross-tenant WRITE of A's words, not a stale label.
  //   `_standupPhase` — ⭐ this one REMOVES A CONTROL. `InputSection` hides
  //     unless the phase is `idle`, so a stale `complete` from A leaves
  //     workspace B with NO standup composer at all, under A's summary card.
  //     A census that only compares VALUES cannot see a missing control.
  //   `_standupFollowUps` — orphaned dependent: chips parsed out of A's
  //     analysis, rendered beside the (already scoped) summary.
  //   `_standupCheckedDate` — the plane's "derived for THIS tenant, today"
  //     stamp; see its default for why the rollover gate changed with it.
  //   `_standupOpenIds` — pointer at rows `_dailyUpdates` empties.
  '_dailyDraft',
  '_standupPhase',
  '_standupFollowUps',
  '_standupCheckedDate',
  '_standupOpenIds',
  // Tickets board prefs — storage went per-workspace (245f9761), so the
  // once-per-SESSION seed latch + the seeded root values are now tenant
  // data: carrying them across a switch pinned workspace A's board shape
  // onto B. The wipe resets the latch; the PHASE-2 tickets branch (and any
  // later board mount) re-seeds from the NEW workspace's storage blob.
  '_ticketsPrefsSeeded',
  '_ticketsFreeform',
  '_colCount',
  '_hiddenCols',
  // The Task Settings "excluded from counts" columns (PR #287): the same
  // per-workspace board pref as `_hiddenCols` (boardPrefs.js _ROOT_TO_PREF).
  '_excludedCols',
  '_showDrafts',
  // Marketplace installed set — same shape: per-workspace storage behind a
  // per-session hydrate latch (hydrateInstalledFromPrefs re-runs on the next
  // marketplace mount).
  'installed',
  '_installedHydrated',
  // A workspace preset's apply (marketplace functions/workspacePresets.js):
  // its lines and result name ONE workspace's apps, so a switch wipes it to
  // its default `null` — every reader treats null as "nothing applied here
  // in this session". The public preset registry beside it is unscoped
  // (state.js).
  '_presetApply',
  // PageSlot minimize-to-dock: parked-app routes are tenant data (each entry
  // names another workspace's app surface). The wipe resets the list; the
  // PageSlot host sweep (canvas/components/PageSlot.js — wsId-stamped hosts)
  // unmounts the kept-alive DOM in the same switch, and MinimizedDock
  // re-seeds the NEW workspace's list from ITS OWN sessionStorage blob.
  '_minimizedSlots',
  // PageSlot pane ROUTES (WS-SWITCH-RESET-MISSES-SLOTS-AND-DRAFT-1,
  // 2026-10-23). `_canvasSlot` is the route the shell PageSlot opens in
  // place and `_canvasSlotSecond` is the split pane's — full tenant paths
  // ('/tickets/<id>', '/documents/<id>', '/u/<slug>', a module route with
  // its query), so an un-wiped pane re-mounted workspace A's page under
  // workspace B. They were in state.js's WORKSPACE_UNSCOPED_KEYS on the
  // claim that "the navigation the switch performs" closes them; it does
  // not — the switch's URL sync rewrites only the `/w/<org>/<ws-handle>`
  // prefix (functions/orgSwitchUrl.js) and keeps every route segment, and
  // the wsId host sweep (canvas/components/PageSlot.js) unmounts A's
  // kept-alive DOM only to have the Slot mount a FRESH host for the same A
  // route. Every sibling pane key below was already scoped, so the pane
  // kept its route while its own back/forward history was wiped under it.
  // NO PHASE-2 refetch pair is owed: a closed pane needs no data, and
  // onWorkspaceChange reads both keys into its surface-active snapshot
  // BEFORE the PHASE-1 replace (pinned by
  // __tests__/slotRouteTenantScope.test.js).
  '_canvasSlot',
  '_canvasSlotSecond',
  // PageSlot per-pane back/forward history + apps-rail flags — a history
  // stack names tenant routes (same reasoning as _minimizedSlots), and an
  // open rail must never greet the next workspace's first slot open.
  '_slotHistPrimary',
  '_slotHistSecond',
  '_slotRailPrimary',
  '_slotRailSecond',
  // The pane pin names a pane of THIS workspace's slot — a switch drops it.
  '_slotPinned',
  '_navActivePath',
  // WS-SWITCH-INCOMPLETE-1 (2026-08-20): the GLOBAL NAVBAR SEARCH plane left
  // quarantine here. `navSearchResults` holds hits fanned out over the ACTIVE
  // workspace's tickets, channels, messages, events, notes, KB and files —
  // `workspaceProject.search` rpc plus the client-side people/docs/files
  // passes, every one of them tenant-filtered server-side.
  // `_navSearchGrouped` is `_groupResults(navSearchResults)`: THE SAME ROWS,
  // re-shaped, and the only thing IntranetAppHeader's dropdown actually
  // renders. `_navSearchActiveIndex` is a flat index INTO those rows.
  // ⛔ THE NAME LIED, AND state.js's OWN QUARANTINE COMMENT REPEATED THE LIE —
  // it cited `_navSearchGrouped` in its "some of these read as UI" list,
  // beside `_slotFullscreen` and `pickedElements`. It is a projection of
  // tenant rows, not a display toggle; the writer settles it in one line
  // (`_navSearchGrouped: _groupResults(all)` in the same `root.update({…})`
  // that writes `navSearchResults: all`). That comment is corrected in place.
  // ⭐ THE LEAK IS REACHABLE, NOT THEORETICAL: the dropdown kept offering
  // workspace A's tasks and notes after the switch, and Enter on a
  // still-highlighted row called `openInSlot` with A's href — the shell then
  // opened another tenant's ticket inside workspace B's slot.
  // Safe to wipe with NO PHASE-2 refetch pair: the correct post-switch value
  // of a search result set IS the empty one, and the next keystroke refills
  // it. No module-level memo guards these three — globalSearch's two caches
  // (`_docsSearchCache` / `_filesSearchCache`) are already workspace-KEYED, so
  // they self-invalidate rather than stranding a wiped key.
  // ⛔ `_navSearchActiveIndex` wipes to -1, its declared default, NOT to 0.
  // `_freshEmpty` passes scalars through, so this is already right — do not
  // "normalize" it with a WORKSPACE_SCOPED_OVERRIDES entry. A 0 would leave
  // NavSearchDropdown's `_navSearchActiveIndex === s._flatIndex` highlight
  // lit on the first row of the NEXT query before any key is pressed.
  // ⛔ THE SIBLING FLAGS STAY UNSCOPED ON PURPOSE. `navSearchQuery` is the
  // user's own typed text, and `_navSearchOpen` / `_navSearchLoading` are
  // transport. That is exactly why scoping alone is not the whole fix: an
  // UNSCOPED query text survives the switch unchanged, so globalSearch's only
  // in-flight guard (`root.navSearchQuery !== query`) still MATCHES after
  // A -> B and a reply issued in A would land in B. The race is closed
  // separately, by functions/sameWorkspaceWriter.js at the terminal write.
  'navSearchResults',
  '_navSearchGrouped',
  '_navSearchActiveIndex',
  // The ⌘K chips are tenant values too (a customer's name, an environment of
  // THIS fleet) — a switch empties them with the rows they filtered.
  '_navSearchChips',
  // WS-SWITCH-INCOMPLETE-1 (2026-08-20): the tickets ACTIVITY plane left
  // quarantine here. `_activityData` is the reply to `workspaceProject.activity`
  // heatmap, and the server scopes that RPC by the caller's user_email AND the
  // workspace_id on the JWT — so it is this user's activity INSIDE THE ACTIVE
  // WORKSPACE, a different year-grid in every tenant. `_activitySelectedDate` +
  // `_activityDetails` are the drill-down: the day the user clicked and that
  // day's rows, which carry `source_table` / `source_id` / `metadata` naming
  // workspace A's tickets and notes outright.
  // ⭐ THE SURFACE IS NOT NICHE: `TicketsActivityHeatmap` is bare-keyed on the
  // HOME dashboard (pages/main.js) and inside MyViewMain, so after a switch the
  // first screen of the new workspace rendered the previous tenant's
  // contribution grid.
  // ⛔ THIS ONE NEEDED A PHASE-2 PAIR AND DID NOT HAVE ONE. The only fetch was
  // `onCreate`, and the shell does not tear the widget down on a tenant
  // boundary, so wiping alone would have left an empty year-grid until the user
  // navigated away and back — a leak fix that becomes a blank surface. The
  // re-derivation ships with this commit: ActivityHeatmap now refetches on a
  // workspace change, stamped per ELEMENT and keyed on the TENANT (never a
  // boolean latch, which is the memo class that strands a wiped key).
  '_activityData',
  '_activitySelectedDate',
  '_activityDetails',
  // WS-SWITCH-INCOMPLETE-1 (2026-08-20), bucket C: the two keys the quarantine
  // comment in state.js named as "reads as UI". Both were re-checked against
  // their WRITERS, and only one of them was even a UI key.
  // ⭐ `pickedElements` IS NOT UI AND THE LEAK IS DISCLOSURE. It is the AI
  // assistant's attached context; a picked TICKET entry carries
  // `{ id, priority, assignee, epic, repository }` (TicketDetailModal's
  // AskAIBtn) — a tenant row plus a MEMBER'S EMAIL — and EventDetailModal
  // pushes calendar events the same way. `sendPrompt` snapshots the whole array
  // onto the outgoing turn as `context.pickedElements`, so a ticket picked in
  // workspace A was TRANSMITTED as context on a prompt typed in workspace B.
  // Its only two resets are the AI session change and the post-send clear;
  // neither fires on a tenant boundary. Wipes to `[]`, its declared default and
  // the "chip stack empty" state assistantSimone.js already documents — no
  // PHASE-2 pair is owed.
  // ⚠️ `preview` declares its OWN `pickedElements` on a DIFFERENT root
  // (preview/state.js). This registry does not reach it, and preview has no
  // workspace switch to leak across. Do not "finish" this by editing preview.
  'pickedElements',
  // `aiAttached` is `pickedElements`' twin and carries the SAME disclosure:
  // an attached EntityRef names a tenant row ('ref:record:vault.items/i-1'),
  // an attached canvas payload carries that board's node titles and statuses,
  // and simoneAiTransport puts both on the outgoing turn as
  // `context.attached`. Nothing else resets it — the attachments deliberately
  // survive a turn (see functions/aiAttached.js) — so the tenant boundary is
  // the only place they can be dropped.
  // ⛔ WIPES TO `{}`, not to `{ refs: [], canvas: null }`: `_freshEmpty`
  // (scopes.js) returns a bare object for an object default. That is why
  // `readAiAttached` is the single reader and normalizes both shapes; no
  // OVERRIDES entry is owed.
  'aiAttached',
  // `_aiConfidenceOpen` is keyed by CONVERSATION MESSAGE ID, and a workspace
  // switch changes the conversation entirely. It carries no tenant data (it
  // is a map of booleans), so this is hygiene rather than a disclosure fix —
  // but a stale entry can only ever pre-expand a row that no longer exists,
  // and wiping it costs nothing. Wipes to `{}`, which is both `_freshEmpty`'s
  // result for an object default AND the declared default, so no OVERRIDES
  // entry is owed.
  '_aiConfidenceOpen',
  // `_aiLevelOpen` — the level pill's disclosure map, keyed the same way and
  // wiped for the same reason as `_aiConfidenceOpen` just above.
  '_aiLevelOpen',
  // `_aiUndoError` — keyed by the created item's id in a thread of this
  // workspace; a switch leaves no other workspace's failure on a row.
  '_aiUndoError',
  // `_aiAskCompose` — keyed by the question card's id in a thread of this
  // workspace; a switch leaves no other workspace's open field or Change on a
  // card. Wipes to `{}`, its declared default: no OVERRIDES entry is owed.
  '_aiAskCompose',
  // ⛔ `_slotFullscreen` — RIGHT VERDICT, WRONG REASON IN THE DRAIN PLAN, which
  // argued it in as one "whose value is a slot ROUTE". It is not a route; it is
  // a TRI-STATE BOOLEAN (workspaceProjects.js says so): true/false record an
  // explicit header-toggle choice, null means "no choice this session". The
  // route is `_canvasSlot`.
  // THE REAL ARGUMENT IS THE FALLBACK ORDER: getSlotFullscreen reads this key
  // FIRST and only falls back to the persisted preference when it is null —
  // and that preference is ALREADY tenant-keyed ('slotFullscreenPref.' + wsId).
  // So a leftover `true` from workspace A SHADOWED workspace B's own saved
  // preference, including the admin-rail expanded default.
  // ⭐ The component's own contract already decides this: "close/minimize/
  // route-away paths reset to null — a close is not an opinion about the next
  // open." A switch is a stronger boundary than a route-away.
  // ⛔ WIPES TO null, NOT false. `false` is an EXPLICIT "not fullscreen" and
  // would beat B's saved expanded default — the exact artefact
  // workspaceProjects.js already has healing code for. The declared default is
  // null, so `_freshEmpty` is already right; do not add an OVERRIDES entry.
  // No PHASE-2 pair is owed: the tenant-keyed preference IS the re-derivation.
  '_slotFullscreen',
  // ─────────────────────────────────────────────────────────────────────────
  // WS-SWITCH-INCOMPLETE-1 drain step 6 — BUCKET B, THE OVERLAY POINTERS, and
  // it is ONE QUESTION ASKED SEVENTEEN TIMES. Answering it seventeen times is
  // how a rule drifts, so the rule is written here once and the whole bucket
  // lands as one diff. See __tests__/bucketBOverlayPointers.test.js.
  //
  // ⭐ THE RULE. An overlay or selection pointer is WORKSPACE-SCOPED when its
  // value is drawn from workspace-scoped data — a row, a row id, or a LABEL
  // that exists only in that workspace's data. It is UNSCOPED when its value
  // comes from a vocabulary the SOURCE defines — a mode, a dock position, a
  // tab, a boolean — because that vocabulary is byte-identical in every
  // workspace. Mechanically: take the writer, ask where the written value
  // comes from. A server row, or `root.<X>` where X is scoped ⇒ scoped. A
  // string literal in the source ⇒ unscoped. Free text the USER typed
  // (peopleQuery) stays unscoped — it is the user's intent,
  // not the tenant's data.
  //
  // ⭐ WHY THE WHOLE BUCKET OWES NO PHASE-2 PAIR: for a pointer the correct
  // post-switch value IS "closed", and every key below already declares a
  // closed default above (null). The wipe restores the
  // closed state by construction, so the module-memo hazard that gates
  // buckets A1 and E cannot apply here. No OVERRIDES entry is owed either.
  //
  // ⛔ FOUR NAMES THAT READ LIKE THIS BUCKET ARE NOT IN IT, and the reasons
  // are recorded so nobody "completes" it by adding them on the name alone:
  // `selectedDocument` — EVERY write in the tree sets it null, and
  // functions/pageContext.js says so itself ("No editor reads
  // `selectedDocument`, so the old bare update was dead"); it has readers and
  // no writer, so it cannot carry workspace A's data ⇒ bucket D, by deletion.
  // `_openThreads` — written on `host`, marketplace component state ⇒ D.
  // `target`/`targetSelected`/`targetOptions` — the Simone wsBridge family ⇒
  // bucket E, held until the live diff. `noGeneralTarget` — a preview/state.js
  // default with no writer ⇒ D.
  // ⛔ `_profileSlug`/`_profileViewed` DO carry tenant rows and are still not
  // here: they are a PAGE plane with its own onRemove teardown
  // (shared/pages/userProfile.js), which is a different argument from a
  // pointer with none. They get their own diff.
  //
  // Tenant ROWS held open by a pointer — the overlay must close on a switch.
  'selectedAnnouncement',
  'selectedEvent',
  'selectedNoteId',
  // The note being dragged onto the notes sidebar, and the row under it (a
  // folder key can be a folder this tenant created). Closed (null) is the
  // right post-switch value, the same as the pointers around it.
  '_notesDragId',
  '_notesDropKey',
  // The note opened beside the open one (a reference's split) names a tenant
  // row by id. Closed (null) after a switch, like the pointers around it.
  '_notesRef',
  // The share-to-chat picker names a tenant note, its search is UI state of
  // the page a switch leaves, and a send in flight names a tenant channel.
  // Closed / empty after a switch.
  '_noteShareTarget',
  '_noteShareQuery',
  '_noteShareBusy',
  'selectedPerson',
  // ⛔ FORM 3 — INVISIBLE TO A LITERAL GREP, which is why both of these read
  // as "no writer" in every census this ticket ran. PersonCard.js writes them
  // as `s.root.update({ [_selField(s.kind)]: s.name })`, where `_selField`
  // RETURNS THE KEY NAME. `selectedGroup` holds a team LABEL out of this
  // workspace's roster; `selectedRole` holds a role name out of
  // `root.roles.available`, and `roles` is itself scoped (drained at step 2),
  // so this is a pointer into a set this registry already wipes.
  // ⭐ selectedGroup's consequence is VISIBLE, not a stale filter chip:
  // pages/people.js prints it verbatim — `No members of "<selectedGroup>" are
  // in this organization's roster.` — so workspace A's team name was rendered
  // on workspace B's own empty state.
  'selectedGroup',
  'selectedRole',
  // Pointers at a row in THIS org's people roster. An email is a global
  // identity, but the OVERLAY it holds open is the current org's member card,
  // and _pendingDmEmail is a cross-page handoff that would otherwise open the
  // DM composer in the wrong tenant.
  '_personPopoverEmail',
  // Its stashed display name, and the party-by-email cache those cards read
  // — party rows are tenant facts of the org that resolved them.
  '_personPopoverName',
  '_partyByEmail',
  '_pendingDmEmail',
  'profileEmail',
  // A decision ROW id from the AI assistant's decision queue.
  '_decisionClarifyId',
  // ─────────────────────────────────────────────────────────────────────────
  // WS-SWITCH-INCOMPLETE-1 drain step 3 — BUCKET A2, THE REMINDERS COMPOSER.
  // Bucket A2's premise holds for all of these: a draft's correct
  // post-switch value IS its declared default, so no PHASE-2 refetch pair
  // is owed and the module-memo hazard cannot apply. What the premise does
  // NOT decide is WHICH SET each key joins, and two of the five did not go
  // where the bucket name suggested.
  //
  // ⭐ THE RULE THIS DOMAIN ADDS — FOLLOW THE VALUE FORWARD TO ITS SINK,
  // not only backward to its source. Step 6's overlay-pointer rule traces
  // BACKWARD and says free text the USER typed stays unscoped
  // (peopleQuery). Applied backward-only, `_reminderDraft`
  // is user-typed text and reads UNSCOPED — and that is wrong. The
  // difference is the SINK. A query's sink is a FILTER over the current
  // view: it survives a switch harmlessly, and wiping it would throw away
  // the user's own intent. A draft's sink is a WRITE into the ACTIVE
  // workspace — `submitReminderDraft` hands the text straight to
  // `createReminder`. A surviving draft does not look stale; it BECOMES
  // workspace B's row carrying workspace A's content. Same authorship,
  // opposite verdict.
  // ⭐ And it carries tenant data literally too: `applyReminderMention`
  // splices a person's first name out of THIS workspace's roster into the
  // text, and `createReminder` then fires a 'mention' notification to that
  // person's email address.
  '_reminderDraft',
  // ⭐ LOCKSTEP. Only ever written together with the draft above — the
  // submit clear, the failed-insert rollback and the row itself all treat
  // the two as ONE fact. Wipe the title alone and workspace B's composer
  // shows an EMPTY title with workspace A's due-date chip still rendered
  // (ReminderComposer hides that chip on `!_reminderDraftDue`, nothing
  // else), and the next reminder typed in B silently inherits A's date.
  // ⛔ A PARTIAL WIPE OF A LOCKSTEP PAIR IS WORSE THAN NO WIPE, because it
  // produces a state no writer can produce and it looks clean.
  '_reminderDraftDue',
  // ⭐ RECLASSIFIED unscoped → scoped, and the only reviewed key this diff
  // moves. It is not an independent input: it is the trailing '@token' OF
  // `_reminderDraft` — a DERIVED CURSOR INTO a value this commit wipes.
  // Left behind, the suggestion popover renders over an empty composer
  // against workspace B's roster; its only render gate is
  // `_reminderMentionQuery == null || no matches`. THE PRECEDENT IS THIS
  // TICKET'S OWN: step 5 part 2 scoped `_navSearchActiveIndex` for exactly
  // this reason — the cursor moves with the value it indexes.
  '_reminderMentionQuery',
  // ⭐ NOT A DRAFT AT ALL — A PER-WORKSPACE CAPABILITY GATE, and the
  // concrete user-visible leak of this domain. pages/reminders.js sets it
  // only on a 403 `forbidden_collection_not_granted` from the records
  // plane, for the ACTIVE workspace id, and `reminderEmptyText` then prints
  // "Reminders isn't enabled for this workspace" verbatim. Carry it across
  // and workspace B — where reminders ARE granted — tells the user its own
  // feature is switched off. Its declared default is `false`, so
  // `_freshEmpty` is already right; no OVERRIDES entry is owed. No PHASE-2
  // pair either: the page's declarative fetch re-runs with the new
  // workspace id and re-decides the gate itself.
  '_remindersLoadError',
  // ─────────────────────────────────────────────────────────────────────────
  // WS-SWITCH-INCOMPLETE-1 drain step 3 — BUCKET A2, THE BROADCAST COMPOSER,
  // and it holds the strongest leak this ticket has found: a CROSS-TENANT
  // WRITE, not a stale form. `openBroadcastComposer(post)` seeds the draft
  // from an announcement ROW and keeps its `id`. `saveBroadcastDraft` then
  // branches on exactly that field — `const operation = draft.id ? 'update'
  // : 'create'` — and issues `sdk.execute('workspaceProject.announcements',
  // 'update', { id: draft.id, payload })`. Open workspace A's release, switch
  // to workspace B, press Publish: the call carries WORKSPACE A'S ROW ID on a
  // session whose active workspace is B. Best case a confusing rejection;
  // bad case workspace A's published announcement is rewritten from B, with
  // `author` resolved from the CURRENT session. ⛔ Nothing else closes it —
  // the only clear paths are a successful save and closeBroadcastComposer,
  // and neither fires on a tenant boundary.
  '_broadcastDraft',
  // ⭐ THE ORPHAN RULE, AND IT IS NOW THE SAME RULE THREE TIMES ON THIS
  // TICKET, so it is named here once. A key whose meaning is only defined
  // RELATIVE to a value this wipe empties must be wiped WITH it, or the
  // switch leaves UI pointing at nothing: `_navSearchActiveIndex` (step 5
  // part 2) a cursor into wiped results, `_reminderMentionQuery` (above) a
  // cursor into a wiped draft, and this — an error BANNER about a wiped
  // draft, whose only render gate is its own presence
  // (`hide: !_broadcastSaveError`). THE MECHANICAL TEST: can this key's
  // value be READ without the value it describes? If not, it is a dependent,
  // and dependents move with their parent. It is written in lockstep at both
  // ends too — the open nulls it while seeding, the save writes the pair.
  '_broadcastSaveError',
  // Follows the retired built-in `bookmarksTag` (step 6; Bookmarks is the
  // App Store app system/bookmarks-app now) for the same reason:
  // `broadcastTags` builds the entire filter rail by counting `post.tag`
  // over `root.announcements` — scoped rows. In workspace B the carried tag
  // names nothing, `broadcastItemsForView` filters EVERY post out, and the
  // page reads "no news" in a workspace that has news, behind a chip that
  // cannot be satisfied. ⛔ ITS DEFAULT IS NOT FALSY — it is 'all', and
  // `_freshEmpty` returns it unchanged; no OVERRIDES entry is owed. No
  // PHASE-2 pair either: the vocabulary re-derives from `announcements`,
  // which this registry already wipes and the page already refetches.
  // ⛔ `_broadcastVisibleCount` IS DELIBERATELY NOT HERE, though
  // `setBroadcastTag` co-writes it. Co-write is evidence, never the verdict:
  // it has three independent writers, so it is not read as one fact with the
  // tag, and a carried page size only shows more of workspace B'S OWN posts.
  '_broadcastTag',
  // ── WS-SWITCH-INCOMPLETE-1 step 8 — BUCKET E, one argued diff (2026-08-21).
  // Thirty-two keys leave WORKSPACE_SCOPE_UNREVIEWED for here, by FAMILY and
  // on each family's WRITER + SINK. Thirteen more went to
  // WORKSPACE_UNSCOPED_KEYS (state.js) in the same diff, and what stays
  // quarantined is named there (31 dead defaults = bucket D, 4 livekit =
  // MEET-LIVE-CALL-SURVIVES-SWITCH-1). Every wipe value below is the declared
  // default through `_freshEmpty` and is a value SOME WRITER ALREADY PRODUCES
  // — no OVERRIDES entry is owed by this step. Live: 32 of 32 carried a
  // seeded marker across a real :4506 switch on the unmoved registry, both
  // directions. See __tests__/bucketETenantScope.test.js.
  //
  // ① TICKET COMPOSER — `ticketDraft` (the previous survivor control, drained
  // LAST of bucket A2 here) is the open ticket form: title/body/labels/
  // epicName/assigneeEmail of workspace A, and `_pendingDeps` holds REAL A
  // ticket ids that `_save` attaches as dependency links after create. Sink
  // rule, the `_broadcastDraft` shape again. Wipes to `null`, the value
  // `_close`/`_cancelEdit`/`resolutions.onCreate` already write (with
  // `selectedTicket: null, ticketEditMode: false` in the same patch).
  // `_commentDrafts` is the unsent comment HTML keyed BY TICKET ROW ID
  // (`_commentDraftKey(root)` = selectedTicket.id, TicketDetailModal); after a
  // switch an id collision in B restores A's text into B's composer and
  // `_postComment` POSTs it to B's ticket. Wipes to `{}`. (The marketplace
  // feed writes a key of the same NAME on its island host, not on root.)
  'ticketDraft',
  '_commentDrafts',
  // ② TICKET CACHE — `recentTickets` is up to 20 FULL ticket rows of workspace
  // A, kept after close by design (functions/ticketDetailModal.js header) and
  // read as a PEER of the scoped `tickets`/`myTickets` lists by
  // `_findTicketInRootLists` and chat's `##number` resolver — a B `##123` that
  // matches a cached A row opens (and `_save` PATCHes) the foreign ticket.
  // Wipes to `[]`; re-seeds on the next modal open, no PHASE-2 pair.
  'recentTickets',
  // ③ DOCUMENTS EDITOR — `editorDoc` carries a doc/note/KB ROW ID plus its
  // full body, and documents/functions/saveNote.js `_savePass` UPDATEs that
  // `doc.id` (or `fileNodeId`). It wipes to `null`, the close value every
  // Back/delete path writes. ⛔ THE AUTOSAVE RACE IS CLOSED BY THE WIPE
  // VALUE, not by a guard: `_savePass` reads `root.editorDoc` at pass time and
  // returns on `!doc`, and re-reads it LIVE after the request, so a timer that
  // fires after the switch writes nothing. (`editorMode` stays unscoped: a UI
  // view/edit lens — with `editorDoc` null neither EditorArea renders.)
  'editorDoc',
  // ④ CANVAS VERSION HISTORY — `_versions` is stamped `{ projectId, branch,
  // items[] … }` from workspace A's project, and `confirmRestore` writes to
  // that captured id: `restoreProjectVersion(cur.projectId, selected._id)` —
  // a destructive PROJECT write aimed by a stale carrier. Wipes to `null`;
  // `ensureVersionsLoaded` re-seeds from the panel's own render, so the
  // unscoped `_versionsOpen` tri-state can stay open harmlessly.
  '_versions',
  // ⑤ CHAT MESSAGE SEARCH — the bare `searchResults` is chat's
  // `workspaceProject.chat.search` hits (message bodies + channel ids of the
  // active workspace), NOT the navbar plane (`navSearchResults`, drained in
  // step 5). Wipes to `[]`, the value `searchMessages`' empty-query reset and
  // `openSearchModal`/`onClose` already write. `searchQuery` stays unscoped
  // under the UI-input rule; an old query over an empty result list is a
  // re-searchable form, not A's rows.
  'searchResults',
  // ⑥ ADD-APP / EXTENSION BUILDER — `_addAppProjectId` is the id of a
  // workspace-bound extension Project (created with `workspaceId`), and
  // functions/extensionSaveShare.js reads `workspaceId = activeWorkspaceId(root)`
  // (the NEW workspace) but `existingId = root._addAppProjectId` (the OLD
  // one's project), then `ai.updateExtension(existingId, { workspaceId, … })`
  // — a stale id after a switch RE-PARENTS A's extension into B and overwrites
  // its source. The cleanest cross-tenant write in this bucket. Wipes to
  // `null` (hydrateAddAppThread's give-up value). Its `_ax_*` thread below
  // hangs off it and re-hydrates on the next `selectAddAppExtension`.
  '_addAppProjectId',
  // The builder's "Open <app>" link points at workspace A's app (its page or
  // its thread); carried into B it would open A's app from B's builder. Wipes
  // to `null`, its declared default (the line it belongs to then shows none).
  '_addAppSaveLink',
  // The composer '+' menu's Build-app hand-off draft (forceBuildApp →
  // pages/add-app.js consume). A pending draft is a message INTO the
  // workspace it was typed in — carrying it across a switch would auto-send
  // A's prompt into B's build chat the moment /add-app opens there. Wipes
  // to `null` (the consumed value).
  '_addAppAutoSend',
  // Build mode is armed against the workspace whose apps it would build —
  // carrying it silently reroutes the FIRST send in workspace B into the
  // build pipeline. Wipes to `false`.
  '_aiBuildMode',
  // ⑦ PROFILE PAGE PLANE — split by CONTENT, not prefix (the other three
  // `_profile*` keys are unscoped in state.js). `_profileViewed` is a ROW
  // LIFTED FROM THE WIPED ROSTER: `_resolveLocal` resolves it out of
  // `peopleAll`/`people`, both registered above, so a surviving copy outlives
  // its own source list. `_profileActivity` is the `workspaceProject.activity`
  // heatmap — per-workspace rows keyed only by email. Both wipe to the values
  // the page's own onCreate/onRemove write (`null`, `{}`). PHASE-2 pair
  // (PROFILE-PAGE-NO-REHYDRATE-ON-SWITCH-1, fixed): the loaders moved out of
  // shared/pages/userProfile.js into shared/functions/userProfileData.js, so
  // onWorkspaceChange re-runs `hydrateUserProfile(root, { write: put })` when
  // `profileSurfaceActive` — the same hydration the page runs on mount. A
  // same-route switch no longer leaves the empty shell behind.
  '_profileViewed',
  '_profileActivity',
  // ⑧ `birthdays` — a PROJECTION of the roster (`person.birthday` over
  // `people`/`peopleAll`, no network): derived data follows its source, and
  // the source is registered above. Wipes to `[]`; onWorkspaceChange's roster
  // put re-derives it (`_deriveBirthdays`, the boot derivation shared).
  'birthdays',
  // ⑨ SIMONE CONVERSATION CORE (general drawer, prefix ''). SERVER-BACKED PER
  // MEMBER PER WORKSPACE: functions/_simone/sessions.js `pinWorkspace` sets
  // `ai.updateContext({ activeWorkspaceId })` before every list/get/create
  // and dispatch routes to `/core/agents/workspaces/:wsId/conversations`;
  // the registered `_aiThreads`/`assistantMessages` block above is the same
  // call already made ("aiChat threads are WORKSPACE-scoped", 2026-07-14).
  // `chatMessages` = the verbatim transcript; `sessions` = the thread index
  // (titles are A's first messages) with message buffers; `activeSessionId` =
  // a conversation id that 404s under B's wsId or continues A's thread;
  // `activeProcesses`/`runHistory` = sub-agent runs whose `agentId`s
  // team-and-agents matches against the CURRENT roster (a surviving batch
  // lights "working now" on B's agents). `currentAssistantId` is a per-turn
  // transport pointer, not rows — it moves by the ORPHANED-DEPENDENTS rule
  // (its meaning is an id in `chatMessages`, wiped in the same tick, or the
  // ReasoningPill points at a removed row). Wipe values `[]`/`{}`/`null` are
  // what `newSession`/`deleteSession`/`session_reset` already write. PHASE-2
  // pair: onWorkspaceChange re-runs `hydrateServerSessions('general')` when
  // the drawer was open or held a thread (`hadSimoneSessions`).
  'chatMessages',
  'sessions',
  'activeSessionId',
  'activeProcesses',
  'runHistory',
  'currentAssistantId',
  // ⑩ `_ax_*` — the SAME six keys under the add-app prefix (`k = '_ax_'`,
  // selected by `chatScope: 'addapp'`, mounted only by pages/add-app.js): the
  // per-EXTENSION thread, project-scoped to an extension that
  // `ai.listExtensions({ workspaceId })` resolved — workspace data one hop
  // out, hanging off `_addAppProjectId` (⑥). `selectAddAppExtension` already
  // writes `_ax_chatMessages: [], _ax_activeSessionId: null` as one patch —
  // the wipe mirrors it. No shell-side PHASE-2 pair: the pointer is nulled,
  // the page re-hydrates on its next select.
  '_ax_chatMessages',
  '_ax_sessions',
  '_ax_activeSessionId',
  '_ax_activeProcesses',
  '_ax_runHistory',
  '_ax_currentAssistantId',
  // ⑪ ASK-USER DECISIONS — `askUserDecisions` is full TICKET ROWS (JWT
  // active_workspace_id-scoped `sdk.tickets.list`, aiAssistant.js says so)
  // rendered as approve/reject cards; `_dismissedDecisionIds` is A's ticket
  // ids used as a SUPPRESSION FILTER in fetchAskUserDecisions — carried into
  // B it can hide B's cards on an id collision; `_decisionClarifyText` is the
  // reason text whose sink is `sdk.tickets.update(ticketId, …)` under the
  // registered `_decisionClarifyId` (the `_wsInviteEmail` shape), and
  // `_decisionClarifyIntent` is the form mode that is meaningless without
  // that id (ORPHANED DEPENDENTS). Wipe values `[]`/`[]`/''/`null` are the
  // component's own submit/cancel values. PHASE-2 pair: onWorkspaceChange
  // re-runs `fetchAskUserDecisions` when the panel was open or held rows.
  'askUserDecisions',
  '_dismissedDecisionIds',
  '_decisionClarifyText',
  '_decisionClarifyIntent',
  // ⑫ AI ACTION CHAIN — `_aiActionProposals` is produced by a dispatch pinned
  // to A's `activeWorkspaceId`, and ConfirmBtn runs `ai.executeActions(
  // actions)` against the THEN-CURRENT SDK context: a surviving proposal
  // executes A's actions inside B. `_aiAnswerPages` is the workspace agent's
  // rendered answer pages, stamped `__extensionApp: { projectId }` from A's
  // extension and persisted back as an A extension by extensionSaveShare.
  // `_aiActionError` (the card's error text) and `_aiActionResults` (the
  // executed results, written once, read by nothing today) move with their
  // producer — ORPHANED DEPENDENTS again: a stale error under B's next card,
  // and A's result rows waiting for a future reader. All wipe to `null`, the
  // value the card's backdrop/Reject/success paths already write.
  // ⑮ MAIL — the §3.2a setup gate and the §3.4 account surface
  // (MAIL-UI-CONNECT-SETTINGS-1). Every one of these is a TENANT FACT:
  // `mailSetup` carries workspace A's policy, provider list, admin flag and
  // mailbox ADDRESSES, and `mailAccounts` is the address list itself — left
  // standing across a switch, workspace B's /settings card and drawer paint
  // A's mailboxes until B's own read returns. The three transient keys
  // (loading / error / notice + the two busy latches) are ORPHANED DEPENDENTS
  // of those two: a 429 "an admin was notified" belongs to the org it was
  // sent in, and a save-in-flight latch keyed to A's account id would block
  // B's rows. Declared + owned by @symbo-ls/workspace-mail/state.js, so their
  // reset values are mirrored in WORKSPACE_SCOPED_OVERRIDES below — the shell
  // must not reach into another package's state file.
  'mailAccounts',
  // /admin/mail's cached accounts and their load state are one tenant read.
  // null re-arms its cache guard; false releases an old workspace's request
  // latch so the next workspace can issue its own read immediately.
  'mailAdminAccounts',
  'mailAdminAccountsLoading',
  'mailAdminAccountsError',
  '_mailReplySending',
  // The compose draft and its open flag: a half-written message names the
  // workspace it was written in — its recipients are that tenancy's people —
  // so it must not reopen over the next workspace. `openMailCompose` (the
  // §3.2a gate entry point) is the first writer of both.
  'mailComposeDraft',
  'mailComposeOpen',
  // The compose lifecycle around that draft (MAIL-UI-COMPOSE-1) — ORPHANED
  // DEPENDENTS of `mailComposeDraft`: a save/send error, an in-flight busy
  // latch, an open recipient menu and the Cc fold all describe A's draft,
  // which the wipe above just cleared. `mailOutbox` rows are A's queued
  // sends (subjects + recipient addresses) — tenant facts like the rest.
  'mailComposeSaving',
  'mailComposeSavedAt',
  'mailComposeError',
  'mailComposeBusy',
  'mailRecipientPicker',
  'mailOutbox',
  '_mailComposeCcOpen',
  '_mailComposeMenu',
  'mailSetup',
  'mailSetupLoading',
  'mailSetupError',
  'mailSetupNotice',
  'mailSetupBusy',
  'mailSettingsDraft',
  'mailSettingsBusy',
  'mailSettingsError',
  // The mail READ path (MAIL-UI-READ-1) — thread rows, envelopes, bodies and
  // the view/selection state are all tenant facts (subjects, addresses,
  // snippets of workspace A). The `_mail*` view latches are ORPHANED
  // DEPENDENTS keyed by A's thread/message ids. `_mailStream` stays OUT on
  // purpose: subscribeMailStream owns that handle's lifecycle (close +
  // re-key on drift) — a registry wipe would drop the reference without
  // closing the EventSource.
  'mailFolders',
  'mailThreads',
  'mailActiveAccountId',
  'mailActiveFolder',
  'mailActiveThreadId',
  'mailMessages',
  'mailBodies',
  'mailUnreadCounts',
  // The SHELL badge signal derived from the map above (architecture/MAIL.md
  // §3.6) — `navUnreadCounts.mail`, read by AppNavbarButton.UnreadBadge and by
  // the header envelope's dot. It drained from state.js's
  // WORKSPACE_SCOPE_UNREVIEWED to here on 2026-09-06, and the evidence is the
  // writer, not a switch diff: that quarantine's stated reason was "its only
  // writer is IntranetAppHeader, which nothing renders", and mail's stream
  // reducer (withMailNavUnread) is now a LIVE writer running at shell scope on
  // every route. What it writes is a TENANT FACT — workspace A's unread inbox
  // total, over A's accounts — so left standing it lights a red dot over B's
  // envelope for mail B cannot open. Scoped as a WHOLE key because the wipe is
  // per key: the map's other half (`chat`) still has no live writer, and `{}`
  // is the right reset for both halves. The stream re-opens on the tenant
  // re-key and its `mail.snapshot` frame refills the count for B, so the wipe
  // needs no PHASE-2 refetch pair.
  'navUnreadCounts',
  'mailSelection',
  'mailFilters',
  'mailSearchQuery',
  'mailThreadLoading',
  'mailThreadError',
  '_mailCursorId',
  '_mailMessageOpen',
  '_mailQuotedOpen',
  // Deep integrations (§3.7, MAIL-INTEGRATIONS-1) — the same ORPHANED-
  // DEPENDENT class: an in-flight integration act on A's thread, and the
  // link-menu pane/query latches over A's channel list.
  '_mailIntegrationBusy',
  '_mailLinkMenuView',
  '_mailShareQuery',
  '_aiActionProposals',
  // Page mode's availability is a fact about THIS workspace's installed apps
  // (the generated-board vocabulary = installed apps ∩ grants), so a switch
  // wipes it until the next workspace's vocabulary answers.
  '_aiBoardsAvailable',
  // The vocabulary itself is the same per-workspace fact — workspace B must
  // never offer starter chips built from A's installed apps.
  '_aiBoardVocab',
  '_aiAnswerPages',
  // ⑯ THE GENERATED BOARD (DESIGN-WL-ASSISTANT-MAIN-VIEW-1). `_aiBoard` is
  // built from workspace A's installed apps and A's data (its cards mount A's
  // module bodies, its blocks carry numbers the model read from A), and its
  // Pin / Save write INTO the active workspace — a surviving board would pin
  // A's widget into B. `_aiBoardHistory` holds A's prompts and specs (the
  // thread chips), `_aiBoards` A's saved-board records. `_aiBoardPreview` is a
  // UI mode over the CURRENT workspace's apps (the fixture is validated
  // against them) — a switch ends the look, like a close. Every late write of
  // a superseded request is already a no-op (`requestId`), so the wipe is the
  // whole story: each resets to its own declared default (null / [] / [] /
  // false), no OVERRIDES entry, no PHASE-2 refetch owed (the boards row
  // re-lists on its next mount).
  '_aiBoard',
  '_aiBoardHistory',
  '_aiBoards',
  '_aiBoardPreview',
  '_aiBoardExplicit',
  // The LIVE DECK — the same class as the board: built from A's installed apps,
  // its records blocks read A's rows, its Save writes INTO the active
  // workspace. A switch wipes it to its declared default (null / false / 0).
  '_aiDeck',
  '_aiDeckPreview',
  '_aiDeckPreviewIndex',
  // FLY MODE's session — A's pages, A's thread: a switch wipes it to null
  // (the stored session of the next workspace comes back on the canvas's
  // mount, functions/assistantFly.js assistantFlyRestore).
  '_aiFly',
  '_aiFlyWorkspace',
  '_phoneNavPanel',
  '_phoneNavError',
  '_phoneNavOperation',
  '_phoneComposerOpen',
  '_phoneNavHist',
  '_filesRailOpen',
  '_canvasRailOpen',
  '_moduleRailOpen',
  // The records answers are workspace A's rows (A's viewings, A's invoices)
  // and the block saves name A's boards: both wipe to `{}`, their declared
  // default, and a late answer for A drops itself (assistantRecordsEnsure
  // re-checks the workspace before it writes).
  '_aiRecords',
  '_aiBlockSaves',
  // The form requests name workspace A's saved rows (A's deal, A's task):
  // wipe to `{}`, their declared default.
  '_aiForms',
  // The broken app-row marks name workspace A's installed apps (A's CRM
  // version); workspace B may run another version of the same app, so a mark
  // from A must not hide B's row. Wipes to `{}`, its declared default.
  '_aiRecordRowsBroken',
  // The popover's dismissed-answer id names a message of workspace A's thread.
  '_aiAnswerPopoverDismissed',
  // The main thread's URL marker and its link state name a conversation of
  // workspace A. The reconcile reads this wipe: the open thread's `?ai=` is
  // removed from the address bar, a link that was never open is read again.
  '_aiThreadUrlId',
  '_aiThreadLink',
  // The home Brief's AI answer speaks about workspace A's signals; a switch
  // wipes it to `null` and the new workspace's signals ask again (a late
  // answer for A drops itself: the ask's key names the workspace).
  '_homeSummaryAi',
  // The home's last Ask / Create mode is workspace A's own (it carries A's
  // id, and homeViewGroupSelect already starts over for any other id). A
  // switch wipes it to `null`, its declared default; B's first menu pick
  // starts B's.
  '_homeViewRecent',
  // A module page's composer latch names workspace A's module and page; a
  // switch wipes it to `null`, its declared default, and the composer shows.
  '_moduleComposerHidden',
  // The first-run answer says whether workspace A is activated and how far
  // its four steps are; carried into B it would offer B A's steps. Wipes to
  // `null`; B's start screen asks again, and a late answer for A drops itself
  // (the read re-checks the workspace before it writes).
  '_flyFirstRun',
  // The builder's cost estimate says what workspace A can pay and whether A's
  // first-build waiver covers the turn; carried into B it would price B's
  // build with A's balance. Wipes to `null`; B's composer asks again, and a
  // late answer for A drops itself (it must still be the newest ask, for the
  // same place, when it lands).
  '_addAppEstimate',
  // The setup job, its first thread and its first build name workspace A;
  // carried into B they would show A's steps over B's builder. Wipes to
  // `null`; B's builder asks again, and a late answer for A drops itself (the
  // read re-checks the workspace before it writes).
  '_workspaceSetup',
  // The build board names workspace A's steps and links; carried into B it
  // would show A's board over B's start screen. Wipes to `null`; B asks again
  // (a 404 there is "no board"), and a late answer for A drops itself (the
  // read re-checks the workspace before it writes).
  '_pobBuild',
  // ⑰ THE HOME SIGNALS (DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1). `_homeSignals`
  // holds workspace A's counts (A's overdue follow-ups, A's unread mail) and
  // the providers A has installed; carried into B it would greet B with A's
  // numbers — and the contract says the cache is PER WORKSPACE. Wipes to
  // `null`, its declared default; the greeting head's own pass refills it for
  // B (its `stateDeps` read the workspace id), and every late answer of A's
  // pass drops itself (the collector re-checks the workspace before a write).
  '_homeSignals',
  '_aiActionError',
  '_aiActionResults',
  // ⑬ MEET ANALYSIS LATCH — `analyzeError` is the analysis-failure text for
  // `activeRoomId`, and BOTH auto-analyze kicks (meet/pages/index.js,
  // TranscriptPage onCreate) SUPPRESS while it is set: A's error blocks the
  // analyze for B's room. It is the error banner of the registered
  // `analysis`, so it moves with it (the `_approvalError` precedent). Wipes
  // to `null`, the Analyze/Regenerate button's own reset value.
  'analyzeError',
  // ⑮ SITE BUILDER — the site on screen, its status, the running write's
  // label, its draft mark, the page it shows and the last AI project write
  // all name THIS workspace's projects. A switch leaves the builder, so each
  // wipes to its cold-boot default (null, null, null, false, null, null).
  '_previewSite',
  '_previewSiteStatus',
  '_previewSiteStep',
  '_previewSiteDraft',
  'activePage',
  '_aiProjectWrite'
])

// Reset values that intentionally DIVERGE from the cold-boot default
// declared above (or, for threadOpen/threadParentId, are owned by a
// DIFFERENT package's state.js — chat, a shared library — mirrored here
// only so this registry has a value to wipe to without this shell reaching
// into another package's state file). Every entry is a conscious historical
// exception, verified against the pre-registry hand-written wipe — do not
// "normalize" these to the cold-boot default without auditing every
// consumer first.
export const WORKSPACE_SCOPED_OVERRIDES = {
  _workspaceActivation: null,
  // Cold-boot default above is `[]`; the switch-reset has always used
  // `null` instead, mirroring feedItems' "not fetched this session yet"
  // sentinel convention (no current consumer reads myTickets' null-vs-[]
  // distinction, but preserved byte-identical rather than assumed-safe).
  myTickets: null,
  // WS-SWITCH-INCOMPLETE-1 (2026-08-20): cold-boot default above is
  // `{ available: [], byUser: {}, version: 0 }`, but the switch-reset MUST be
  // `null`, and this is the one override whose value changes behaviour rather
  // than just shape. `_freshEmpty` (scopes.js) collapses EVERY object to `{}`,
  // so a "shaped empty" override is impossible here and would be a false edit
  // that merely looks like a safeguard. It also would not be the right value:
  // TWO independent call sites restore the full shape with the idiom
  // `root.roles || { available: [], byUser: {}, byUserMemberId: {}, version: 0 }`
  // — loadPrivateData `_loadRoles` and PersonCard's optimistic role write. A
  // bare `{}` is TRUTHY, so it silently DEFEATS both fallbacks and leaves the
  // object permanently missing the fields they exist to restore. `null` is
  // falsy, so both fire as designed, and `null` is already the state every
  // consumer handles — it is the pre-`_loadRoles` value AdminChrome documents
  // ("`s.root.roles` stays undefined until `_loadRoles` resolves"). Every reader
  // found in packages/** guards with `root?.roles?.X || {}` / `|| []` or
  // `root.roles && root.roles.X`.
  roles: null,
  // Cold-boot default above is `{}` (meet/pages/index.js keys it by room
  // id: `waitingRoom[roomId]`); the switch-reset has always used `[]`
  // instead. Pre-existing type mismatch, not introduced by this registry —
  // preserved as-is rather than changed as a drive-by fix.
  waitingRoom: [],
  // Declared + owned by @symbo-ls/workspace-mail/state.js (a shared library),
  // so the shell's own stateDefaults carry no entry and the wipe would write
  // `undefined` — which v3.14's schema check refuses. Mirrored here, exactly
  // as the analytics keys below are; keep in sync with that file.
  mailAccounts: [],
  mailAdminAccounts: null,
  mailAdminAccountsLoading: false,
  mailAdminAccountsError: null,
  mailComposeDraft: null,
  mailComposeOpen: false,
  mailSetup: null,
  mailSetupLoading: false,
  mailSetupError: null,
  mailSetupNotice: null,
  mailSetupBusy: null,
  mailSettingsDraft: {},
  mailSettingsBusy: null,
  mailSettingsError: null,
  mailComposeSaving: false,
  mailComposeSavedAt: null,
  mailComposeError: null,
  mailComposeBusy: null,
  mailRecipientPicker: null,
  mailOutbox: [],
  _mailComposeCcOpen: false,
  _mailComposeMenu: null,
  mailFolders: {},
  mailThreads: {},
  mailActiveAccountId: null,
  mailActiveFolder: 'inbox',
  mailActiveThreadId: null,
  mailMessages: {},
  mailBodies: {},
  mailUnreadCounts: {},
  mailSelection: [],
  mailFilters: { unread: false, starred: false, attachments: false, from: null, label: null },
  mailSearchQuery: '',
  mailThreadLoading: null,
  mailThreadError: null,
  _mailCursorId: null,
  _mailMessageOpen: {},
  _mailQuotedOpen: {},
  _mailIntegrationBusy: null,
  _mailLinkMenuView: 'actions',
  _mailShareQuery: '',
  // Declared + owned by chat/state.js — keep in sync with it if its
  // defaults ever change.
  threadOpen: false,
  threadParentId: null,
  // Keys with NO cold-boot default declaration (written ad-hoc by their
  // feature surfaces) — the wipe needs an explicit reset shape or it would
  // write `undefined` (2026-07-30 isolation-audit additions).
  filesSelectedIds: [],
  filesViewer: null,
  // Analytics package keys — declared in @symbo-ls/workspace-analytics/
  // state.js (merged via sharedLibraries), so the shell's own
  // stateDefaults carry no entry for them; explicit reset shapes keep the
  // wipe from writing `undefined` (2026-08-08 analytics-merge audit).
  _analytics: null,
  _analyticsSessions: null,
  _analyticsActiveUsers: null,
  _analyticsServerSource: null,
  _analyticsSampleSize: 0,
  _analyticsProjectKey: null,
  _analyticsParams: null,
  _wsaWeekly: null,
  _wsaBugs: null,
  _wsaOpenTasks: null,
  _wsaActive: null,
  logsUsers: [],
  logsSessions: [],
  logsEvents: [],
  logsNetwork: [],
  logsVerbose: [],
  logsBugs: [],
  logsSessionDetail: null,
  logsSessionEvents: [],
  _logsSessionId: null,
  _logsCountry: null,
  _logsUser: null,
  // WS-ANALYTICS-FAMILY-SPLIT-UTC-TOTALS-1 feeds — null is each feed's own
  // "not read yet" sentinel (analytics/state.js), so the honest-state rule
  // (only a positively read feed prints a number) holds across a switch.
  _analyticsTotals: null,
  _analyticsTotalsError: null,
  _analyticsPages: null,
  _analyticsReferrers: null,
  _analyticsRollupTz: null,
  // WS-ANALYTICS-UTM-CAMPAIGNS-UI-1 feeds + drill filters — null is each
  // feed's "not read yet" sentinel and each filter's "no filter"
  // (analytics/state.js).
  _analyticsCampaigns: null,
  _analyticsCampaignsError: null,
  _analyticsDaily: null,
  _analyticsDailyError: null,
  _logsUtmSource: null,
  _logsUtmMedium: null,
  _logsUtmCampaign: null,
  _logsVisitor: null,
  _wsaTotals: null,
  _wsaTotalsError: null,
  // "Not loaded" sentinel — loadWorkspaceApps repopulates in the activation
  // chain; `[]` would read as "loaded, none installed".
  workspaceApps: null,
  // Same sentinel rationale — loadWorkspaceProject repopulates; `{}` would
  // read as "loaded, module-less workspace" and skip module activation UI.
  workspaceProject: null,
  // Recomputed from the new workspace's panels by the widget resync.
  _homeHiddenWidgets: null,
  // Re-resolved from the NEW workspace's prefs row / scoped cache by the home
  // page's own switch hook (pages/main.js `_resolveBootWidgetColors`).
  _homeWidgetColors: null,
  // Transient palette — closed by the switch, never carried.
  _homeColorPicker: null,
  // Re-resolved from the NEW workspace's prefs row / scoped cache by the
  // board's reconcile (pages/main.js `_reconcileHomeBoard`).
  _homeLayout: null,
  // A drag never survives a switch.
  _homeDragId: null,
  financials: null,
  _wsMembers: null,
  _projectsForActiveOrg: null,
  // Tickets board prefs (per-workspace since 245f9761): latch re-arms so the
  // seed re-runs for the new workspace; value keys clear to "unseeded" (the
  // seed writes real defaults from the new workspace's storage blob).
  _ticketsPrefsSeeded: false,
  _ticketsFreeform: null,
  _colCount: null,
  _hiddenCols: null,
  _excludedCols: null,
  _showDrafts: null,
  // WS-SWITCH-INCOMPLETE-1 step 3, bucket A2 (2026-08-21): the two modal
  // drafts wipe to the value their CLOSE path writes, not to `_freshEmpty`'s
  // `{}` of their shaped default. `closeNewChannelModal` and
  // `closeNewRoomModal` both write `null`; every reader already handles it;
  // `{}` is a shape no writer produces. Argued beside their registration.
  newChannelDraft: null,
  newRoomDraft: null,
  // WS-SWITCH-DRAIN-EPICSTREE-FILEPREVIEWS-1. `epicsTree` is declared `null`
  // in tickets/state.js (merged via sharedLibraries, so no entry here); its
  // readers all guard `root?.epicsTree || []` / `?.length`, and `null` — not
  // `[]` — is what lets loadEpicsTree's `!root.epicsTree?.length` refetch for
  // the new tenant. The two file caches are declared nowhere (first-write
  // keys of files/functions/index.js); every reader does
  // `(root._fileThumbs || {})[id]`, so `{}` is the value they expect.
  epicsTree: null,
  _filePreviews: {},
  _fileThumbs: {},
  // WS-SWITCH-INCOMPLETE-1 leg 14. The three pipeline drag pointers, declared
  // in `packages/pipeline/state.js` (merged via the consuming app's context,
  // so no entry in this file's stateDefaults). `null` is not a guess: it is
  // the exact value `pipelineDragEnd` already writes to all three when a drag
  // finishes, and the value `pipeline/state.js` declares — so the wipe hands
  // every reader a value it has already met, and `pipelineDragOverCard`'s
  // `root.pipelineDragId === id` / `root.pipelineDropBeforeId !== id`
  // comparisons behave as they do between drags. `{}` would be a shape no
  // writer in that library produces.
  pipelineDragId: null,
  pipelineDragOverStage: null,
  pipelineDropBeforeId: null,
  // WS-UNDECLARED-KEYS-DRAIN-BATCH-1 (2026-08-21) — five census-backlog keys,
  // declared in state.js's WORKSPACE_SCOPED_KEYS in this same commit. None has
  // a cold-boot default anywhere, so each needs an explicit reset shape here or
  // the wipe would write `undefined`. Each value is the one its own readers
  // already handle:
  //   `projectMembers` — every reader guards `s.root?.projectMembers || []` /
  //     `!s.projectMembers || s.projectMembers.length === 0`, and `null` (not
  //     `[]`) matches its declared PROJECT-scope siblings `project`,
  //     `projectComponents`, `projectPages`, `projectState`, which are all
  //     `null`. It also keeps "never fetched" distinct from "fetched, empty".
  //   `_mcpConnectorDraft` / `roomSettingsDraft` — wipe to the value their own
  //     CLOSE path writes, the same argument `newChannelDraft` /`newRoomDraft`
  //     carry above: both overlays close with `{ …Draft: null }`, and
  //     RoomSettingsModal's seed guard is literally `if (s.root
  //     ?.roomSettingsDraft) return`, so a truthy `{}` would permanently
  //     suppress the re-seed. `{}` is a shape no writer produces.
  //   `activityScoring` — the fetch transform always writes an ARRAY of rows
  //     and both readers do `(s.root?.activityScoring || [])`, so `[]` is the
  //     shape they expect; the table renders its empty state until the new
  //     workspace-project's rows land.
  //   `_usageTurnStats` — read once as `root?._usageTurnStats || {}`; `null` is
  //     the pre-fetch value that reader already handles, and it keeps the
  //     `{ error }` degrade shape from surviving into the next workspace.
  projectMembers: null,
  _mcpConnectorDraft: null,
  activityScoring: [],
  _usageTurnStats: null,
  roomSettingsDraft: null,
  // Marketplace installed set — hydrate latch re-arms; the list empties.
  installed: [],
  _installedHydrated: false,
  // WS-ROOT-STATE-FORM2-WRITE-HOLE-1 (2026-08-30) — Form-2 census drain,
  // batch 2; declared in state.js's WORKSPACE_SCOPED_KEYS in this same
  // commit. None has a cold-boot default, so each needs its reset shape here.
  //   `_agents` — `null`, NOT `[]`: yourAgents.js reads
  //     `Array.isArray(root._agents)` as "loaded", so `[]` would be a FALSE
  //     ZERO ("loaded, no agents") — the exact ambiguity FALSE-ZERO-1 removed
  //     at the producers. `null` re-arms every consumer's fallback and the
  //     seed roster (`_agents(root)` in team-and-agents falls back to
  //     `_seedAgents`). All readers guard `Array.isArray(...)`.
  //   `_agentsLoaded` — `false` re-arms the three shared-gate producers for
  //     the new org (home _loadHomeAgentRoster re-runs from the org-change
  //     onUpdate; the canvas + simone producers re-run on their next mount/
  //     touch — both re-read the gate every call).
  //   `_agentsError` — a failure text about the PREVIOUS org's fetch must not
  //     render `!` badges over the new org's widgets.
  //   `_agentAssignmentsLoaded` — `false`, same re-arm as `_agentsLoaded`
  //     (team-and-agents onCreate re-loads).
  //   `agentAssignmentsByAssignee` / `agentAssignmentsByAgent` — `null`; every
  //     reader guards (`root?.agentAssignmentsByAssignee?.[id]`,
  //     `{ ...(root.agentAssignmentsByAssignee || {}) }`), and the loader
  //     rewrites BOTH maps wholesale, so `null` never survives a load.
  //   `_integrationHub` — `null` is the pre-fetch value `_hubLoaded()` already
  //     distinguishes ("loading vs empty", pages/admin/integrations.js); every
  //     admin page refetches it in onCreate.
  _agents: null,
  _agentsLoaded: false,
  _agentsError: null,
  _agentAssignmentsLoaded: false,
  agentAssignmentsByAssignee: null,
  agentAssignmentsByAgent: null,
  _integrationHub: null,
  // `_adminLogsError` — `false`: the read has not failed. The one reader
  // (pages/admin/logs.js) tests it for truth, and the loader writes it on
  // every read, so a wiped `false` shows the list or its empty line again.
  _adminLogsError: false
}
